import { useState, useEffect, useCallback, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { supabase } from '../lib/supabase';
import { SCORE_FIELDS, normalizeScores, averageScore as avg } from '../lib/scores';

const C = {
  parchment:"#FFFEFC",ink:"#0D0D0D",wheat:"#C8A84B",wheatLight:"#F7F0DC",
  sand:"#C4A882",sandLight:"#F5EFE6",muted:"#8A8070",border:"#E8E0D0",white:"#FFFFFF",
};
const BAKE_TYPES=[
  {id:"bread",label:"Bread"},
  {id:"non-bread",label:"Non-Bread"},
];
const UNITS=["g","kg","ml","L","tsp","tbsp","cup","pcs","pinch"];

const BREAD_KW=["sourdough","bread","loaf","baguette","focaccia","ciabatta","rye","pain","levain","boule","batard","pullman","brioche","milk bread","shokupan","pita","naan","pretzel","bagel","rolls","bun"];
function classifyBakeType(name){
  const n=(name||"").toLowerCase();
  if(BREAD_KW.some(k=>n.includes(k))) return "bread";
  return "non-bread";
}

const FLOUR_KW=["flour","atta","semolina","rye","spelt","wheat","whole","tipo"];
const LIQUID_KW=["water","milk","cream","buttermilk","egg","yolk","juice","beer","kefir","whey"];
function guessType(name){
  const n=(name||"").toLowerCase();
  if(FLOUR_KW.some(k=>n.includes(k))) return "flour";
  if(LIQUID_KW.some(k=>n.includes(k))) return "liquid";
  return "other";
}
function calcTotals(rows){
  let weight=0,flour=0,liquid=0;
  (rows||[]).forEach(r=>{
    if(r._group) return;
    const q=parseFloat(r.qty);
    if(!q||r.unit!=="g") return;
    weight+=q;
    const t=r.typeOverride||guessType(r.name);
    if(t==="flour") flour+=q;
    if(t==="liquid") liquid+=q;
  });
  return {weight,flour,liquid,hydration:flour>0?Math.round((liquid/flour)*100):null};
}
function calcPct(ing,totalWeight,isBread=true){
  const t=ing.typeOverride||guessType(ing.name);
  if(isBread&&t==="liquid") return "—";
  const q=parseFloat(ing.qty);
  if(!q||ing.unit!=="g"||!totalWeight) return "—";
  return Math.round((q/totalWeight)*100)+"%";
}

const today=()=>new Date().toISOString().slice(0,10);
const fmtDate=d=>new Date(d+"T12:00:00").toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"});
const getBakeTypeInfo=id=>BAKE_TYPES.find(t=>t.id===id)||BAKE_TYPES[1];

const METRIC_UNITS=["g","kg","ml","L","l","tbsp","tsp","pcs"];
const AI_IMPORT_SYSTEM=`You are a recipe parser. Extract the recipe name, ingredients, and steps from the provided content (text or one or more images). When multiple images are provided, combine all ingredients found across all images into a single ingredients list, and combine all method steps found across all images into a single steps list — do not duplicate. Apply these unit rules to every ingredient:

RULE 1 — Metric only: ingredient measured in g, kg, ml, or l → import quantity and unit as-is.
RULE 2 — Dual measurement (metric + non-metric): keep only the metric value; discard cups/oz/fl oz/lbs/etc. Exception: if the non-metric side is tbsp or tsp and there is no metric equivalent, keep the tbsp/tsp value.
RULE 3 — Non-metric only using cups/oz/fl oz/lbs: set quantity to "" and unit to "". The user will fill in manually.
RULE 4 — Fully non-metric recipe (no g/kg/ml/l/tbsp/tsp at all): set "blocked": true at the top level.
RULE 5 — tbsp or tsp only: import the quantity and unit as "tbsp" or "tsp" exactly.
RULE 6 — No unit (countable items, e.g. "2 eggs", "3 cloves", "1 vanilla pod"): import quantity as-is and set unit to "pcs".

Allowed unit values: "g", "kg", "ml", "l", "tbsp", "tsp", "pcs". No other units are permitted.

Return ONLY this JSON, no markdown, no code fences, no explanation:
{
  "name": "Classic Sourdough",
  "blocked": false,
  "ingredients": [
    { "name": "bread flour", "quantity": "500", "unit": "g" },
    { "name": "water", "quantity": "375", "unit": "ml" },
    { "name": "salt", "quantity": "1", "unit": "tsp" },
    { "name": "eggs", "quantity": "2", "unit": "pcs" },
    { "name": "butter", "quantity": "", "unit": "" }
  ],
  "steps": [
    "Mix flour and water, rest 30 minutes.",
    "Add salt and starter, fold every 30 mins for 3 hours."
  ]
}
Critical rules:
- Use EXACTLY these key names: "name", "blocked", "ingredients", "steps"
- Each ingredient must have EXACTLY: "name", "quantity", "unit"
- "steps" must be an array of plain strings, not objects
- quantity must always be a string (e.g. "500", not 500)
- If recipe name cannot be determined, use ""
- Return raw JSON only — no markdown, no code fences, no explanation`;

const GRID_SYSTEM=`You map a recipe's method steps onto its ingredients so the recipe can be drawn as a grid: ingredients are rows, steps are columns, and each step brackets exactly the ingredients it works on.

You are given the ingredient list (each with an id, in the order the user has arranged them) and the method steps in order.

For each step, list the ids of EVERY ingredient that step works on — not just new ones.

- A step working on only part of the recipe lists only that part. Whipping egg whites with sugar in a separate bowl lists the egg white and that sugar, nothing else. Its column is blank alongside the other ingredients.
- A step that brings earlier work together lists everything it brings together. "Combine the two mixtures" lists every ingredient in both — do NOT return an empty list just because the step adds nothing new.
- A step acting on the whole mixture (bake, prove, rest, chill, cool, turn out) lists every ingredient in the recipe.
- An empty list is ONLY for a step that happens before any ingredient is touched, such as preheating the oven. Every other step must name ingredients.
- Resolve collective phrases against the actual list: "fold in the dry ingredients" means the specific dry ingredients present.

CRITICAL: the ingredients you list for a single step must sit NEXT TO EACH OTHER in the list you were given. Never reorder the ingredients and never invent ids. If a step's ingredients are scattered and cannot form one unbroken run, list them anyway — the app will detect it and ask the user to reorder.

Keep every step, in the original order. "text" is a 1–3 word lowercase column label — "Melt the butter." becomes "melt", "Fold in the dry ingredients." becomes "fold in". "detail" holds temperature and time if present, e.g. "170°C · 30–40 min", otherwise "".

Return ONLY this JSON, no markdown, no code fences, no explanation:
{
  "steps": [
    { "text": "mix", "detail": "", "ing": ["i1","i2","i3"] },
    { "text": "beat", "detail": "", "ing": ["i4","i5"] },
    { "text": "combine", "detail": "", "ing": ["i1","i2","i3","i4","i5"] },
    { "text": "bake", "detail": "170°C · 30–40 min", "ing": ["i1","i2","i3","i4","i5"] }
  ]
}`;

// --- Method grid ---
// A grid is [{id, text, detail, ing:[ingredientId]}] stored on recipe.method_grid.
// Each step lists every ingredient it works on, so a step that handles only part
// of the recipe brackets only that part and stays blank alongside the rest.
function gridRows(ings){
  const list=ings||[];
  const groups=list.filter(i=>i._group);
  const out=list.filter(i=>!i._group&&!i.groupId);
  groups.forEach(g=>out.push(...list.filter(i=>!i._group&&i.groupId===g.id)));
  return out;
}
function buildSpans(steps,rows){
  const idx={};rows.forEach((r,i)=>{idx[r.id]=i;});
  const n=rows.length;
  const spans=[];
  const seen=new Set();
  let bad=null;
  (steps||[]).forEach(s=>{
    const pos=(s.ing||[]).map(id=>idx[id]).filter(i=>i!==undefined);
    // A step naming nothing is a step acting on everything so far — combine,
    // bake, rest. Only a step before any mixing has happened stays blank.
    if(!pos.length){
      if(!seen.size){spans.push(null);return;}
      const so=[...seen];
      spans.push([Math.min(...so),Math.max(...so)]);
      return;
    }
    pos.forEach(p=>seen.add(p));
    const set=new Set(pos);
    const lo=Math.min(...pos),hi=Math.max(...pos);
    for(let i=lo;i<=hi;i++) if(!set.has(i)&&!bad) bad=s;
    spans.push([lo,hi]);
  });
  return {spans,bad,n};
}
function gridIsDrawable(steps,rows){
  if(!steps||!steps.length||!rows.length) return false;
  return !buildSpans(steps,rows).bad;
}

// --- Photo utilities ---
async function loadPhotosForEntity(entityType, entityId) {
  const col = entityType === 'recipe' ? 'recipe_id' : 'bake_id';
  const { data } = await supabase.from('photos').select('*').eq(col, entityId).order('created_at');
  if (!data) return [];
  return data.map(p => ({
    id: p.id,
    url: supabase.storage.from('photos').getPublicUrl(p.storage_path).data.publicUrl,
    caption: p.caption || '',
    storage_path: p.storage_path,
    pending: false,
  }));
}

// --- Shared UI primitives ---
function Label({children}){
  return <p style={{fontSize:10,letterSpacing:"0.06em",textTransform:"uppercase",color:C.muted,margin:"0 0 6px",fontFamily:"Poppins,sans-serif"}}>{children}</p>;
}
function Inp({value,onChange,placeholder,style}){
  return <input value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
    style={{width:"100%",boxSizing:"border-box",fontSize:13,padding:"9px 11px",border:`0.5px solid ${C.border}`,borderRadius:8,fontFamily:"Poppins,sans-serif",color:C.ink,background:C.white,outline:"none",...(style||{})}}/>;
}
function Tarea({value,onChange,placeholder,rows,className}){
  return <textarea value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} rows={rows||6} className={className||''}
    style={{width:"100%",boxSizing:"border-box",fontSize:13,padding:"9px 11px",border:`0.5px solid ${C.border}`,borderRadius:8,fontFamily:"Poppins,sans-serif",color:C.ink,background:C.white,resize:"vertical",outline:"none",lineHeight:1.7}}/>;
}
function Btn({children,onClick,primary,danger,disabled}){
  return <button onClick={onClick} disabled={disabled} style={{
    padding:"11px 14px",border:primary?"none":danger?`0.5px solid #c0392b`:`0.5px solid ${C.border}`,
    borderRadius:8,background:primary?C.wheat:"none",color:primary?"#fff":danger?"#c0392b":C.muted,
    fontSize:11,cursor:"pointer",fontFamily:"Poppins,sans-serif",letterSpacing:"0.04em",opacity:disabled?0.4:1,
  }}>{children}</button>;
}
function SmBtn({children,onClick,active}){
  return <button onClick={e=>{e.stopPropagation();onClick();}} style={{
    background:"none",border:`0.5px solid ${active?C.wheat:C.border}`,borderRadius:6,cursor:"pointer",
    color:active?C.wheat:C.muted,fontSize:10,padding:"4px 8px",fontFamily:"Poppins,sans-serif",letterSpacing:"0.04em",whiteSpace:"nowrap",flexShrink:0,
  }}>{children}</button>;
}
function ScoreBar({label,value,onChange}){
  return <div style={{display:"flex",alignItems:"center",gap:10}}>
    <input aria-label={label} type="range" min={1} max={5} step={1} value={value} onChange={e=>onChange(+e.target.value)}
      style={{flex:1,accentColor:C.wheat,background:C.parchment,appearance:"none",height:3,borderRadius:2,outline:"none"}}/>
    <span style={{fontSize:13,fontWeight:500,color:C.ink,minWidth:36,textAlign:"right",fontFamily:"Poppins,sans-serif"}}>{value} / 5</span>
  </div>;
}

function TotalsBar({rows,bakeType}){
  const t=calcTotals(rows);
  const isBread=bakeType==='bread';
  if(!t.weight&&!t.flour&&!t.liquid) return null;
  const items=isBread
    ?[{label:"Total",val:`${t.weight}g`},{label:"Flour",val:t.flour>0?`${t.flour}g`:"—"},{label:"Liquid",val:t.liquid>0?`${t.liquid}g`:"—"},{label:"Hydration",val:t.hydration!==null?`${t.hydration}%`:"—"}]
    :[{label:"Total",val:`${t.weight}g`}];
  return(
    <div style={{background:C.sandLight,borderRadius:8,padding:"10px 12px",marginBottom:14,display:"grid",gridTemplateColumns:isBread?"1fr 1fr 1fr 1fr":"1fr",gap:6}}>
      {items.map(({label,val})=>(
        <div key={label} style={{textAlign:"center"}}>
          <p style={{margin:0,fontSize:9,letterSpacing:"0.06em",textTransform:"uppercase",color:C.muted,fontFamily:"Poppins,sans-serif"}}>{label}</p>
          <p style={{margin:"3px 0 0",fontSize:13,fontWeight:300,color:C.ink,fontFamily:"Poppins,sans-serif"}}>{val}</p>
        </div>
      ))}
    </div>
  );
}

function MethodGrid({recipe,adj,editable,onChange,plain}){
  const adjusted=adj||{};
  const [open,setOpen]=useState(null);
  const rows=gridRows(recipe.ingredients);
  const steps=recipe.method_grid||[];
  const {spans,bad,n}=buildSpans(steps,rows);
  if(!rows.length||!steps.length) return null;

  if(bad) return(
    <div style={{borderLeft:`2px solid #c0392b`,background:"#FBF1EF",padding:"12px 14px",marginTop:12,fontFamily:"Poppins,sans-serif"}}>
      <p style={{margin:"0 0 3px",fontSize:11,letterSpacing:"0.05em",textTransform:"uppercase",color:"#c0392b"}}>Can't draw this one yet</p>
      <p style={{margin:0,fontSize:12,color:C.ink,lineHeight:1.6}}>
        “{bad.text}” uses ingredients that aren't next to each other in your list. Drag them together, then build the grid again.
      </p>
    </div>
  );

  const cellsAt=r=>{
    const out=[];
    spans.forEach((sp,c)=>{
      if(!sp){if(r===0) out.push({c,pad:true,rs:n});return;}
      const [lo,hi]=sp;
      if(r===0&&lo>0) out.push({c,pad:true,rs:lo});
      if(r===lo) out.push({c,pad:false,rs:hi-lo+1});
      if(r===hi+1&&hi<n-1) out.push({c,pad:true,rs:n-1-hi});
    });
    return out.sort((a,b)=>a.c-b.c);
  };

  const W={ing:196};
  // width on a table cell excludes padding, so the pinned column only measures
  // what it declares if it is border-box.
  // plain drops the sticky positioning for export snapshots, which never scroll
  const frozen={background:C.parchment,boxSizing:"border-box",width:W.ing,minWidth:W.ing,boxShadow:`1px 0 0 ${C.border}`,
    ...(plain?{}:{position:"sticky",left:0,zIndex:1})};
  const td={padding:"9px 8px",borderBottom:`0.5px solid ${C.border}`,fontSize:13,color:C.ink,verticalAlign:"top"};
  const th={padding:"0 8px 7px",fontSize:9,letterSpacing:"0.06em",textTransform:"uppercase",color:C.muted,textAlign:"left",fontWeight:400,borderBottom:`0.5px solid ${C.border}`,whiteSpace:"nowrap"};

  function setStep(k,patch){
    onChange(steps.map((s,i)=>i===k?{...s,...patch}:s));
  }
  function toggleIng(k,id){
    const has=(steps[k].ing||[]).includes(id);
    onChange(steps.map((s,i)=>i!==k?s:{...s,ing:has?(s.ing||[]).filter(x=>x!==id):[...(s.ing||[]),id]}));
  }

  return(
    <div style={{fontFamily:"Poppins,sans-serif"}}>
      <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
        {/* separate borders, not collapse: collapsed borders are painted by the
            table and bleed over the frozen column when the steps scroll under it */}
        <table style={{borderCollapse:"separate",borderSpacing:0,tableLayout:"fixed",width:"100%",minWidth:W.ing+steps.length*64}}>
          <thead>
            <tr>
              <th style={{...th,...frozen}}>ingredient</th>
              {steps.map((s,i)=><th key={i} style={{...th,textAlign:"center",color:C.wheat}}>S{i+1}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((ing,r)=>{
              const a=adjusted[ing.id];
              const isAdj=a!==undefined&&a!==ing.qty;
              const qty=isAdj?a:ing.qty;
              const amount=[qty,ing.unit].filter(v=>String(v||'').trim()).join(' ');
              return(
                <tr key={ing.id}>
                  <td style={{...td,...frozen}}>
                    {ing.name}
                    {amount&&<span style={{color:isAdj?C.wheat:C.muted,fontWeight:isAdj?500:400}}> — {amount}</span>}
                  </td>
                  {cellsAt(r).map(({c,pad,rs})=>pad
                    ?<td key={c} rowSpan={rs} style={{background:C.white}}/>
                    :<td key={c} rowSpan={rs}
                        onClick={editable?()=>setOpen(open===c?null:c):undefined}
                        style={{
                          border:`0.5px solid ${C.border}`,padding:"8px 6px",textAlign:"center",verticalAlign:"top",
                          background:C.wheatLight,fontSize:12,color:C.ink,lineHeight:1.45,
                          cursor:editable?"pointer":"default",
                          outline:open===c?`1.5px solid ${C.wheat}`:"none",outlineOffset:-1.5,
                        }}>
                        {steps[c].text}
                        {steps[c].detail&&<span style={{display:"block",color:C.muted,fontSize:11,marginTop:2}}>{steps[c].detail}</span>}
                      </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editable&&open!==null&&steps[open]&&(
        <div style={{border:`0.5px solid ${C.border}`,borderRadius:10,background:C.white,padding:14,marginTop:12}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <p style={{margin:0,fontSize:10,letterSpacing:"0.06em",textTransform:"uppercase",color:C.muted}}>step S{open+1}</p>
            <span onClick={()=>setOpen(null)} style={{fontSize:16,color:C.muted,cursor:"pointer",lineHeight:1}}>×</span>
          </div>
          <div style={{display:"flex",gap:8,marginBottom:12}}>
            <Inp value={steps[open].text||''} onChange={v=>setStep(open,{text:v})} placeholder="mix"/>
            <Inp value={steps[open].detail||''} onChange={v=>setStep(open,{detail:v})} placeholder="170°C · 30 min"/>
          </div>
          <p style={{margin:"0 0 3px",fontSize:10,letterSpacing:"0.06em",textTransform:"uppercase",color:C.muted}}>ingredients this step works on</p>
          <p style={{margin:"0 0 8px",fontSize:10,color:C.muted,fontStyle:"italic"}}>They must sit next to each other in your list.</p>
          <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:12}}>
            {rows.map(ing=>(
              <label key={ing.id} style={{display:"flex",alignItems:"center",gap:8,fontSize:12,color:C.ink,cursor:"pointer"}}>
                <input type="checkbox" checked={(steps[open].ing||[]).includes(ing.id)} onChange={()=>toggleIng(open,ing.id)} style={{accentColor:C.wheat,margin:0}}/>
                <span>{ing.name}</span>
              </label>
            ))}
          </div>
          <Btn onClick={()=>setOpen(null)}>done</Btn>
        </div>
      )}
    </div>
  );
}

function GridModal({recipe,adj,editable,onChange,onExport,onClose}){
  // Lock background scroll while open (works on iOS Safari too)
  useEffect(()=>{
    const y=window.scrollY;
    document.body.style.position='fixed';
    document.body.style.top=`-${y}px`;
    document.body.style.width='100%';
    return ()=>{
      document.body.style.position='';
      document.body.style.top='';
      document.body.style.width='';
      window.scrollTo(0,y);
    };
  },[]);
  useEffect(()=>{
    const onKey=e=>{if(e.key==='Escape') onClose();};
    window.addEventListener('keydown',onKey);
    return ()=>window.removeEventListener('keydown',onKey);
  });
  return(
    <div onClick={onClose}
      style={{position:'fixed',inset:0,background:'rgba(13,13,13,0.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:300,padding:12,boxSizing:'border-box'}}>
      <div onClick={e=>e.stopPropagation()}
        style={{background:C.parchment,borderRadius:16,padding:'18px 18px 22px',width:'100%',maxWidth:1100,maxHeight:'90vh',overflowY:'auto',boxSizing:'border-box',fontFamily:'Poppins,sans-serif'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:12,marginBottom:14}}>
          <div style={{minWidth:0}}>
            <p style={{margin:0,fontSize:14,fontWeight:300,color:C.ink,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{recipe.name||'Recipe'}</p>
            <p style={{margin:'2px 0 0',fontSize:10,color:C.muted,fontStyle:'italic'}}>
              {editable?'Tap a step to correct it':'Grid'}
            </p>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:10,flexShrink:0}}>
            {onExport&&<SmBtn onClick={onExport}>export</SmBtn>}
            <button onClick={onClose} aria-label="close"
              style={{background:'none',border:'none',fontSize:20,color:C.muted,cursor:'pointer',lineHeight:1,padding:0}}>×</button>
          </div>
        </div>
        <MethodGrid recipe={recipe} adj={adj} editable={editable} onChange={onChange}/>
      </div>
    </div>
  );
}

function IngView({recipe,adj}){
  const adjusted=adj||{};
  const ings=recipe.ingredients||[];
  const adjRows=ings.map(i=>({...i,qty:adjusted[i.id]!==undefined?adjusted[i.id]:i.qty}));
  const totals=calcTotals(adjRows);
  const isBread=recipe.bake_type==='bread';
  const groups=ings.filter(i=>i._group);
  const ungrouped=ings.filter(i=>!i._group&&!i.groupId);
  function renderIng(ing){
    const a=adjusted[ing.id];
    const isAdj=a!==undefined&&a!==ing.qty;
    const adjIng={...ing,qty:isAdj?a:ing.qty};
    const pct=calcPct(adjIng,totals.weight,isBread);
    return(
      <div key={ing.id} style={{display:"grid",gridTemplateColumns:"2fr 1fr 0.7fr 0.6fr",gap:4,marginBottom:7,alignItems:"center"}}>
        <span style={{fontSize:13,color:C.ink,fontFamily:"Poppins,sans-serif"}}>{ing.name}</span>
        <span style={{fontSize:13,color:isAdj?C.wheat:C.ink,fontWeight:isAdj?500:300,fontFamily:"Poppins,sans-serif"}}>{isAdj?a:ing.qty}</span>
        <span style={{fontSize:12,color:C.muted,fontFamily:"Poppins,sans-serif"}}>{ing.unit}</span>
        <span style={{fontSize:12,color:C.muted,fontFamily:"Poppins,sans-serif"}}>{pct}</span>
      </div>
    );
  }
  if(gridIsDrawable(recipe.method_grid,gridRows(ings))) return(
    <div>
      <TotalsBar rows={adjRows} bakeType={recipe.bake_type}/>
      <MethodGrid recipe={recipe} adj={adjusted}/>
      {isBread&&<p style={{fontSize:10,color:C.muted,fontStyle:"italic",margin:"8px 0 0",fontFamily:"Poppins,sans-serif"}}>Refer to Hydration % for liquid ingredients</p>}
    </div>
  );
  return(
    <div>
      <TotalsBar rows={adjRows} bakeType={recipe.bake_type}/>
      <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 0.7fr 0.6fr",gap:4,marginBottom:6}}>
        {["ingredient","qty","unit","%"].map(h=><span key={h} style={{fontSize:9,letterSpacing:"0.06em",textTransform:"uppercase",color:C.muted,fontFamily:"Poppins,sans-serif"}}>{h}</span>)}
      </div>
      {ungrouped.map(renderIng)}
      {groups.map(grp=>{
        const members=ings.filter(i=>!i._group&&i.groupId===grp.id);
        return(
          <div key={grp.id} style={{marginBottom:8}}>
            <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6,marginTop:4}}>
              <span style={{fontSize:10,letterSpacing:"0.06em",textTransform:"uppercase",color:C.muted,fontFamily:"Poppins,sans-serif"}}>{grp.name}</span>
              <div style={{flex:1,height:0.5,background:C.border}}/>
            </div>
            <div style={{paddingLeft:12,borderLeft:`1.5px solid ${C.border}`}}>
              {members.map(renderIng)}
            </div>
          </div>
        );
      })}
      {isBread&&<p style={{fontSize:10,color:C.muted,fontStyle:"italic",margin:"2px 0 10px",fontFamily:"Poppins,sans-serif"}}>Refer to Hydration % for liquid ingredients</p>}
      {recipe.method&&(
        <div style={{marginTop:12,paddingTop:12,borderTop:`0.5px solid ${C.border}`}}>
          <Label>method</Label>
          <p style={{fontSize:12,color:C.ink,lineHeight:1.7,margin:0,whiteSpace:"pre-wrap",fontFamily:"Poppins,sans-serif"}}>{recipe.method}</p>
        </div>
      )}
    </div>
  );
}

// --- Export ---
// An exported grid needs room to breathe, so the card widens with the step count
// instead of being pinned to the 380px list card.
const GRID_EXPORT_STEP_W=104;
const gridExportWidth=recipe=>56+196+((recipe&&recipe.method_grid||[]).length*GRID_EXPORT_STEP_W);

function ExportCard({recipe,adj,grid}){
  if(!recipe) return null;
  if(grid) return <GridExportCard recipe={recipe} adj={adj}/>;
  const adjusted=adj||{};
  const ings=recipe.ingredients||[];
  const adjRows=ings.map(i=>({...i,qty:adjusted[i.id]!==undefined?adjusted[i.id]:i.qty}));
  const totals=calcTotals(adjRows);
  const isBread=recipe.bake_type==='bread';
  const totalsItems=isBread
    ?[{label:"Total",val:`${totals.weight}g`},{label:"Flour",val:totals.flour>0?`${totals.flour}g`:"—"},{label:"Liquid",val:totals.liquid>0?`${totals.liquid}g`:"—"},{label:"Hydration",val:totals.hydration!==null?`${totals.hydration}%`:"—"}]
    :[{label:"Total",val:`${totals.weight}g`}];
  const groups=ings.filter(i=>i._group);
  const ungrouped=ings.filter(i=>!i._group&&!i.groupId);
  function renderIng(ing){
    const a=adjusted[ing.id];
    const adjIng={...ing,qty:a!==undefined?a:ing.qty};
    const pct=calcPct(adjIng,totals.weight,isBread);
    return(
      <div key={ing.id} style={{display:"grid",gridTemplateColumns:"2fr 1fr 0.7fr 0.6fr",gap:4,marginBottom:8,alignItems:"center"}}>
        <span style={{fontSize:13,color:C.ink,fontFamily:"Poppins,sans-serif"}}>{ing.name}</span>
        <span style={{fontSize:13,color:C.ink,fontFamily:"Poppins,sans-serif"}}>{adjIng.qty}</span>
        <span style={{fontSize:12,color:C.muted,fontFamily:"Poppins,sans-serif"}}>{ing.unit}</span>
        <span style={{fontSize:12,color:C.muted,fontFamily:"Poppins,sans-serif"}}>{pct}</span>
      </div>
    );
  }
  const steps=(recipe.method||"").split('\n').filter(s=>s.trim());
  return(
    <div style={{width:380,padding:"32px 28px",background:C.parchment,fontFamily:"Poppins,sans-serif",boxSizing:"border-box"}}>
      <p style={{fontSize:22,fontWeight:200,color:C.ink,margin:"0 0 2px",fontFamily:"Poppins,sans-serif"}}>{recipe.name}</p>
      {recipe.category&&<p style={{fontSize:10,color:C.muted,letterSpacing:"0.06em",textTransform:"uppercase",margin:"0 0 20px",fontFamily:"Poppins,sans-serif"}}>{recipe.category}</p>}
      {totals.weight>0&&(
        <div style={{background:C.sandLight,borderRadius:8,padding:"10px 12px",marginBottom:16,display:"grid",gridTemplateColumns:isBread?"1fr 1fr 1fr 1fr":"1fr",gap:6}}>
          {totalsItems.map(({label,val})=>(
            <div key={label} style={{textAlign:"center"}}>
              <p style={{margin:0,fontSize:9,letterSpacing:"0.06em",textTransform:"uppercase",color:C.muted,fontFamily:"Poppins,sans-serif"}}>{label}</p>
              <p style={{margin:"3px 0 0",fontSize:13,fontWeight:300,color:C.ink,fontFamily:"Poppins,sans-serif"}}>{val}</p>
            </div>
          ))}
        </div>
      )}
      {ings.length>0&&(
        <>
          <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 0.7fr 0.6fr",gap:4,marginBottom:8}}>
            {["ingredient","qty","unit","%"].map(h=><span key={h} style={{fontSize:9,letterSpacing:"0.06em",textTransform:"uppercase",color:C.muted,fontFamily:"Poppins,sans-serif"}}>{h}</span>)}
          </div>
          {ungrouped.map(renderIng)}
          {groups.map(grp=>{
            const members=ings.filter(i=>!i._group&&i.groupId===grp.id);
            return(
              <div key={grp.id} style={{marginBottom:8}}>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6,marginTop:4}}>
                  <span style={{fontSize:10,letterSpacing:"0.06em",textTransform:"uppercase",color:C.muted,fontFamily:"Poppins,sans-serif"}}>{grp.name}</span>
                  <div style={{flex:1,height:0.5,background:C.border}}/>
                </div>
                <div style={{paddingLeft:12,borderLeft:`1.5px solid ${C.border}`}}>
                  {members.map(renderIng)}
                </div>
              </div>
            );
          })}
          {isBread&&<p style={{fontSize:10,color:C.muted,fontStyle:"italic",margin:"2px 0 10px",fontFamily:"Poppins,sans-serif"}}>Refer to Hydration % for liquid ingredients</p>}
        </>
      )}
      {steps.length>0&&(
        <div style={{marginTop:16,paddingTop:16,borderTop:`0.5px solid ${C.border}`}}>
          <p style={{fontSize:9,letterSpacing:"0.06em",textTransform:"uppercase",color:C.muted,margin:"0 0 12px",fontFamily:"Poppins,sans-serif"}}>method</p>
          {steps.map((s,i)=>(
            <div key={i} style={{display:"flex",gap:10,marginBottom:10}}>
              <span style={{fontSize:10,color:C.wheat,fontWeight:500,minWidth:16,fontFamily:"Poppins,sans-serif"}}>{i+1}</span>
              <p style={{fontSize:12,color:C.ink,lineHeight:1.7,margin:0,flex:1,fontFamily:"Poppins,sans-serif"}}>{s}</p>
            </div>
          ))}
        </div>
      )}
      <div style={{marginTop:24,paddingTop:12,borderTop:`0.5px solid ${C.border}`,textAlign:"right"}}>
        <p style={{fontSize:9,color:C.muted,letterSpacing:"0.06em",textTransform:"uppercase",margin:0,fontFamily:"Poppins,sans-serif"}}>homer</p>
      </div>
    </div>
  );
}

function GridExportCard({recipe,adj}){
  const adjusted=adj||{};
  const ings=recipe.ingredients||[];
  const adjRows=ings.map(i=>({...i,qty:adjusted[i.id]!==undefined?adjusted[i.id]:i.qty}));
  const totals=calcTotals(adjRows);
  const isBread=recipe.bake_type==='bread';
  const totalsItems=isBread
    ?[{label:"Total",val:`${totals.weight}g`},{label:"Flour",val:totals.flour>0?`${totals.flour}g`:"—"},{label:"Liquid",val:totals.liquid>0?`${totals.liquid}g`:"—"},{label:"Hydration",val:totals.hydration!==null?`${totals.hydration}%`:"—"}]
    :[{label:"Total",val:`${totals.weight}g`}];
  return(
    <div style={{width:gridExportWidth(recipe),padding:"32px 28px",background:C.parchment,fontFamily:"Poppins,sans-serif",boxSizing:"border-box"}}>
      <p style={{fontSize:22,fontWeight:200,color:C.ink,margin:"0 0 2px",fontFamily:"Poppins,sans-serif"}}>{recipe.name}</p>
      {recipe.category&&<p style={{fontSize:10,color:C.muted,letterSpacing:"0.06em",textTransform:"uppercase",margin:"0 0 20px",fontFamily:"Poppins,sans-serif"}}>{recipe.category}</p>}
      {totals.weight>0&&(
        <div style={{background:C.sandLight,borderRadius:8,padding:"10px 12px",marginBottom:16,display:"grid",gridTemplateColumns:isBread?"1fr 1fr 1fr 1fr":"1fr",gap:6}}>
          {totalsItems.map(({label,val})=>(
            <div key={label} style={{textAlign:"center"}}>
              <p style={{margin:0,fontSize:9,letterSpacing:"0.06em",textTransform:"uppercase",color:C.muted,fontFamily:"Poppins,sans-serif"}}>{label}</p>
              <p style={{margin:"3px 0 0",fontSize:13,fontWeight:300,color:C.ink,fontFamily:"Poppins,sans-serif"}}>{val}</p>
            </div>
          ))}
        </div>
      )}
      <MethodGrid recipe={recipe} adj={adjusted} plain/>
      {isBread&&<p style={{fontSize:10,color:C.muted,fontStyle:"italic",margin:"10px 0 0",fontFamily:"Poppins,sans-serif"}}>Refer to Hydration % for liquid ingredients</p>}
      <div style={{marginTop:24,paddingTop:12,borderTop:`0.5px solid ${C.border}`,textAlign:"right"}}>
        <p style={{fontSize:9,color:C.muted,letterSpacing:"0.06em",textTransform:"uppercase",margin:0,fontFamily:"Poppins,sans-serif"}}>homer</p>
      </div>
    </div>
  );
}

function ExportModal({recipe,adj,filename,grid,onClose}){
  const cardRef=useRef(null);
  const [exporting,setExporting]=useState(false);
  async function doExport(format){
    if(!cardRef.current) return;
    setExporting(true);
    try{
      const h2c=(await import('html2canvas')).default;
      const canvas=await h2c(cardRef.current,{
        backgroundColor:C.parchment,scale:2,useCORS:true,allowTaint:true,logging:false,
        onclone:(doc)=>{
          const el=doc.querySelector('[data-export-root]');
          if(el) el.style.opacity='1';
        },
      });
      if(format==='image'){
        const a=document.createElement('a');
        a.download=`${filename}.png`;
        a.href=canvas.toDataURL('image/png');
        a.click();
      } else {
        const {jsPDF}=await import('jspdf');
        const imgData=canvas.toDataURL('image/png');
        const mmW=210;
        const mmH=(canvas.height/canvas.width)*mmW;
        const pdf=new jsPDF({unit:'mm',format:[mmW,Math.max(mmH,10)]});
        pdf.addImage(imgData,'PNG',0,0,mmW,mmH);
        pdf.save(`${filename}.pdf`);
      }
    }catch(e){console.error('Export error:',e);}
    setExporting(false);
    onClose();
  }
  return(
    <>
      <div data-export-root ref={cardRef} style={{position:'fixed',top:0,left:0,width:grid?gridExportWidth(recipe):380,zIndex:0,opacity:0,pointerEvents:'none',background:C.parchment}}>
        <ExportCard recipe={recipe} adj={adj} grid={grid}/>
      </div>
      <div style={{position:'fixed',inset:0,background:'rgba(13,13,13,0.4)',display:'flex',alignItems:'flex-end',justifyContent:'center',zIndex:300}} onClick={()=>!exporting&&onClose()}>
        <div onClick={e=>e.stopPropagation()} style={{background:C.parchment,borderRadius:'16px 16px 0 0',padding:'24px 20px 36px',width:'100%',maxWidth:420,boxSizing:'border-box',fontFamily:'Poppins,sans-serif'}}>
          <p style={{fontSize:13,fontWeight:300,color:C.ink,margin:'0 0 4px'}}>Export</p>
          <p style={{fontSize:11,color:C.muted,margin:'0 0 20px'}}>{recipe?.name}</p>
          <div style={{display:'flex',gap:10,marginBottom:12}}>
            <button onClick={()=>doExport('image')} disabled={exporting}
              style={{flex:1,padding:'14px 0',border:`0.5px solid ${C.border}`,borderRadius:8,background:'none',color:C.ink,fontSize:12,cursor:'pointer',fontFamily:'Poppins,sans-serif',letterSpacing:'0.04em',opacity:exporting?0.4:1}}>
              Image
            </button>
            <button onClick={()=>doExport('pdf')} disabled={exporting}
              style={{flex:1,padding:'14px 0',border:'none',borderRadius:8,background:C.wheat,color:'#fff',fontSize:12,cursor:'pointer',fontFamily:'Poppins,sans-serif',letterSpacing:'0.04em',opacity:exporting?0.4:1}}>
              PDF
            </button>
          </div>
          {exporting&&<p style={{fontSize:11,color:C.muted,textAlign:'center',margin:'0 0 12px'}}>Exporting…</p>}
          <button onClick={onClose} disabled={exporting} style={{width:'100%',padding:'10px 0',background:'none',border:`0.5px solid ${C.border}`,borderRadius:8,fontSize:11,color:C.muted,cursor:'pointer',fontFamily:'Poppins,sans-serif',letterSpacing:'0.06em',textTransform:'uppercase',opacity:exporting?0.4:1}}>cancel</button>
        </div>
      </div>
    </>
  );
}

function CatManager({categories,onAdd,onDelete,open,onClose}){
  const [input,setInput]=useState("");
  function add(){const v=input.trim();if(!v||categories.includes(v))return;onAdd(v);setInput("");}
  if(!open) return null;
  return(
    <div style={{marginBottom:20,padding:"14px 16px",border:`0.5px solid ${C.border}`,borderRadius:10,background:C.white}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <Label>categories</Label>
        <span onClick={onClose} style={{fontSize:10,letterSpacing:"0.06em",textTransform:"uppercase",color:C.wheat,cursor:"pointer",fontFamily:"Poppins,sans-serif"}}>done</span>
      </div>
      <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10}}>
        {categories.map(c=>(
          <div key={c} style={{display:"inline-flex",alignItems:"center",gap:5,padding:"5px 10px",borderRadius:20,border:`0.5px solid ${C.border}`,background:C.parchment}}>
            <span style={{fontSize:11,color:C.ink,fontFamily:"Poppins,sans-serif"}}>{c}</span>
            <span onClick={()=>onDelete(c)} style={{fontSize:13,color:C.muted,cursor:"pointer",lineHeight:1}}>×</span>
          </div>
        ))}
      </div>
      <div style={{display:"flex",gap:6}}>
        <input value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&add()} placeholder="new category"
          style={{flex:1,fontSize:12,padding:"7px 10px",border:`0.5px solid ${C.border}`,borderRadius:7,fontFamily:"Poppins,sans-serif",color:C.ink,background:C.white,outline:"none"}}/>
        <button onClick={add} style={{padding:"7px 12px",border:`0.5px solid ${C.wheat}`,borderRadius:7,background:"none",color:C.wheat,fontSize:11,cursor:"pointer",fontFamily:"Poppins,sans-serif"}}>add</button>
      </div>
    </div>
  );
}

// --- PhotoStrip ---
function PhotoStrip({photos, onAdd, onDelete, onCaption, onReplace, uploading}){
  const [active,setActive]=useState(null);
  const [captionEdit,setCaptionEdit]=useState('');
  const [replaceId,setReplaceId]=useState(null);
  const addRef=useRef(null);
  const replaceRef=useRef(null);
  const touchStartX=useRef(null);

  const activeIndex=photos.findIndex(p=>p.id===active?.id);

  function openPhoto(photo){setActive(photo);setCaptionEdit(photo.caption||'');}
  function closeModal(){setActive(null);setReplaceId(null);}
  function saveAndClose(){if(active) onCaption(active.id,captionEdit);closeModal();}
  function triggerReplace(id){setReplaceId(id);replaceRef.current?.click();}

  function goPrev(){
    if(activeIndex>0){
      const prev=photos[activeIndex-1];
      setActive(prev);
      setCaptionEdit(prev.caption||'');
    }
  }
  function goNext(){
    if(activeIndex<photos.length-1){
      const next=photos[activeIndex+1];
      setActive(next);
      setCaptionEdit(next.caption||'');
    }
  }

  // Lock background scroll when modal is open (works on iOS Safari too)
  useEffect(()=>{
    if(!active) return;
    const y=window.scrollY;
    document.body.style.position='fixed';
    document.body.style.top=`-${y}px`;
    document.body.style.width='100%';
    return ()=>{
      document.body.style.position='';
      document.body.style.top='';
      document.body.style.width='';
      window.scrollTo(0,y);
    };
  },[active]);

  useEffect(()=>{
    if(!active) return;
    function handleKey(e){
      if(e.key==='ArrowLeft') goPrev();
      else if(e.key==='ArrowRight') goNext();
      else if(e.key==='Escape') closeModal();
    }
    window.addEventListener('keydown',handleKey);
    return ()=>window.removeEventListener('keydown',handleKey);
  },[active,activeIndex,photos.length]);// eslint-disable-line

  function handleTouchStart(e){ touchStartX.current=e.touches[0].clientX; }
  function handleTouchEnd(e){
    if(touchStartX.current===null) return;
    const delta=e.changedTouches[0].clientX-touchStartX.current;
    touchStartX.current=null;
    if(delta>50) goPrev();
    else if(delta<-50) goNext();
  }

  return(
    <div style={{marginBottom:20}}>
      <Label>photos</Label>
      <div style={{display:"flex",gap:8,overflowX:"auto",paddingBottom:6,WebkitOverflowScrolling:"touch"}}>
        {photos.map(p=>(
          <div key={p.id} onClick={()=>openPhoto(p)} style={{flexShrink:0,cursor:"pointer",width:88}}>
            <img src={p.url} alt="" style={{width:88,height:88,objectFit:"cover",borderRadius:10,display:"block"}}/>
            {p.caption&&(
              <p style={{fontSize:10,color:C.muted,margin:"4px 0 0",width:88,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontFamily:"Poppins,sans-serif"}}>{p.caption}</p>
            )}
          </div>
        ))}
        <div
          onClick={()=>!uploading&&addRef.current?.click()}
          style={{flexShrink:0,width:88,height:88,border:`0.5px dashed ${C.border}`,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",cursor:uploading?"default":"pointer",background:uploading?C.sandLight:"none"}}
        >
          {uploading
            ?<span style={{fontSize:10,color:C.muted,fontFamily:"Poppins,sans-serif"}}>…</span>
            :<span style={{fontSize:24,color:C.muted,lineHeight:1,fontWeight:200}}>+</span>}
        </div>
        <input ref={addRef} type="file" accept="image/*" multiple style={{display:"none"}}
          onChange={e=>{onAdd(Array.from(e.target.files));e.target.value='';}}/>
        <input ref={replaceRef} type="file" accept="image/*" style={{display:"none"}}
          onChange={e=>{
            if(e.target.files[0]&&replaceId){onReplace(replaceId,e.target.files[0]);e.target.value='';}
            closeModal();
          }}/>
      </div>

      {active&&(
        <div
          style={{position:"fixed",inset:0,background:"rgba(13,13,13,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:"0 16px",boxSizing:"border-box"}}
          onClick={closeModal}
        >
          <div
            onClick={e=>e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            style={{background:C.parchment,borderRadius:16,padding:"16px 16px 24px",width:"100%",maxWidth:420,boxSizing:"border-box"}}
          >
            {/* Navigation bar */}
            {photos.length>1&&(
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                <button
                  onClick={goPrev}
                  disabled={activeIndex===0}
                  style={{background:"none",border:"none",cursor:activeIndex===0?"default":"pointer",color:activeIndex===0?C.border:C.muted,fontSize:22,padding:"2px 8px",lineHeight:1,fontFamily:"Poppins,sans-serif"}}
                >‹</button>
                <span style={{fontSize:10,color:C.muted,fontFamily:"Poppins,sans-serif",letterSpacing:"0.06em"}}>
                  {activeIndex+1} / {photos.length}
                </span>
                <button
                  onClick={goNext}
                  disabled={activeIndex===photos.length-1}
                  style={{background:"none",border:"none",cursor:activeIndex===photos.length-1?"default":"pointer",color:activeIndex===photos.length-1?C.border:C.muted,fontSize:22,padding:"2px 8px",lineHeight:1,fontFamily:"Poppins,sans-serif"}}
                >›</button>
              </div>
            )}

            {/* Full photo — contain, capped at 65vh */}
            <img
              src={active.url}
              alt=""
              style={{width:"100%",maxHeight:"65vh",objectFit:"contain",borderRadius:10,marginBottom:14,display:"block"}}
            />

            <Label>caption</Label>
            <Inp value={captionEdit} onChange={setCaptionEdit} placeholder="Add a short caption…"/>
            <div style={{display:"flex",gap:8,marginTop:14}}>
              <Btn onClick={()=>triggerReplace(active.id)}>replace</Btn>
              <Btn danger onClick={()=>{onDelete(active.id);closeModal();}}>remove</Btn>
              <Btn primary onClick={saveAndClose}>done</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Drag handle ---
function DragHandle({dragHandleProps, color}){
  return(
    <div
      {...dragHandleProps}
      style={{display:"flex",flexDirection:"column",justifyContent:"center",gap:3,cursor:"grab",padding:"4px 2px",flexShrink:0,touchAction:"none"}}
    >
      {[0,1,2].map(i=>(
        <div key={i} style={{width:12,height:1,background:color||C.muted,opacity:0.45,borderRadius:1}}/>
      ))}
    </div>
  );
}

// --- RecipeEditor ---
function RecipeEditor({recipe,categories,onSave,onCancel,onDelete,deleteMsg,uploadPhoto,deletePhoto,updatePhotoCaption}){
  const blank={id:"",name:"",category:"",method:"",method_grid:null,ingredients:[],is_favourite:false,parent_id:null,variation_name:"",bake_type:""};
  const [r,setR]=useState(()=>{
    const base=recipe?{...blank,...recipe}:blank;
    return {...base,bake_type:base.bake_type||classifyBakeType(base.name),ingredients:(base.ingredients||[]).map(i=>({typeOverride:null,...i}))};
  });
  const [typeOverridden,setTypeOverridden]=useState(!!(recipe&&recipe.bake_type));
  const [importOpen,setImportOpen]=useState(false);
  const [importText,setImportText]=useState('');
  const [importLoading,setImportLoading]=useState(false);
  const [importError,setImportError]=useState('');
  const [gridLoading,setGridLoading]=useState(false);
  const [gridOpen,setGridOpen]=useState(false);
  const [gridExportOpen,setGridExportOpen]=useState(false);
  const [exportOpen,setExportOpen]=useState(false);
  const [toast,setToast]=useState({msg:'',type:''});
  const toastTimerRef=useRef(null);
  const [photos,setPhotos]=useState([]);
  const [photoUploading,setPhotoUploading]=useState(false);
  const [pendingFocusId,setPendingFocusId]=useState(null);

  const nameRefs=useRef({});
  const qtyRefs=useRef({});
  const photoImportRef=useRef(null);

  function showToast(msg,type){
    if(toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({msg,type});
    toastTimerRef.current=setTimeout(()=>setToast({msg:'',type:''}),4000);
  }

  const set=(k,v)=>setR(p=>({...p,[k]:v}));
  const totals=calcTotals(r.ingredients);

  useEffect(()=>{
    if(r.id) loadPhotosForEntity('recipe',r.id).then(setPhotos);
  },[r.id]);// eslint-disable-line

  // Auto-focus newly added ingredient
  useEffect(()=>{
    if(pendingFocusId&&nameRefs.current[pendingFocusId]){
      nameRefs.current[pendingFocusId].focus();
      setPendingFocusId(null);
    }
  },[r.ingredients,pendingFocusId]);

  useEffect(()=>{
    if(!importOpen) return;
    const y=window.scrollY;
    document.body.style.position='fixed';
    document.body.style.top=`-${y}px`;
    document.body.style.width='100%';
    return ()=>{
      document.body.style.position='';
      document.body.style.top='';
      document.body.style.width='';
      window.scrollTo(0,y);
    };
  },[importOpen]);

  function handleNameChange(v){
    set("name",v);
    if(!typeOverridden) set("bake_type",classifyBakeType(v));
  }

  async function handleAddPhotos(files){
    if(r.id){
      setPhotoUploading(true);
      for(const file of files){
        const photo=await uploadPhoto('recipe',r.id,file,'');
        if(photo) setPhotos(prev=>[...prev,photo]);
      }
      setPhotoUploading(false);
    } else {
      const newPhotos=files.map(file=>({
        id:`pending-${Date.now()}-${Math.random()}`,
        url:URL.createObjectURL(file),
        caption:'',file,pending:true,
      }));
      setPhotos(prev=>[...prev,...newPhotos]);
    }
  }

  async function handleDeletePhoto(id){
    const photo=photos.find(p=>p.id===id);
    if(!photo) return;
    if(photo.pending){URL.revokeObjectURL(photo.url);}
    else{await deletePhoto(photo);}
    setPhotos(prev=>prev.filter(p=>p.id!==id));
  }

  async function handleCaptionSave(id,caption){
    setPhotos(prev=>prev.map(p=>p.id===id?{...p,caption}:p));
    const photo=photos.find(p=>p.id===id);
    if(photo&&!photo.pending) await updatePhotoCaption(id,caption);
  }

  async function handleReplacePhoto(id,newFile){
    await handleDeletePhoto(id);
    await handleAddPhotos([newFile]);
  }

  const gridSteps=(r.method||"").split('\n').map(s=>s.trim()).filter(Boolean);
  const canBuildGrid=gridSteps.length>0&&gridRows(r.ingredients).length>0;
  const hasGrid=!!(r.method_grid&&r.method_grid.length);

  async function handleBuildGrid(){
    setGridLoading(true);
    try{
      const rows=gridRows(r.ingredients);
      const brief={
        ingredients:rows.map(i=>({id:i.id,name:i.name,quantity:i.qty,unit:i.unit})),
        steps:gridSteps,
      };
      const res=await fetch("/api/import-recipe",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({model:"claude-haiku-4-5-20251001",max_tokens:1024,system:GRID_SYSTEM,messages:[{role:"user",content:JSON.stringify(brief)}]}),
      });
      if(!res.ok) throw new Error(`${res.status}`);
      const data=await res.json();
      const raw=data.content?.[0]?.text?.trim()||'';
      const p=JSON.parse(raw.replace(/^```(?:json)?\n?/,'').replace(/\n?```$/,''));
      const valid=new Set(rows.map(i=>i.id));
      const seen=new Set();
      const out=(p.steps||[]).map((s,i)=>({
        id:`s${Date.now()+i}`,
        text:String(s.text||'').trim()||`step ${i+1}`,
        detail:String(s.detail||'').trim(),
        ing:(Array.isArray(s.ing)?s.ing:[]).filter(x=>{
          if(!valid.has(x)||seen.has(x)) return false;
          seen.add(x);return true;
        }),
      }));
      if(!out.length) throw new Error('no steps returned');
      set('method_grid',out);
      setGridOpen(true);
      showToast('Grid built, please review','success');
    }catch(err){
      console.error("Grid build failed:",err);
      showToast("We couldn't build a grid for this one",'error');
    }finally{
      setGridLoading(false);
    }
  }

  async function handleImport(){
    setImportLoading(true);setImportError('');
    try{
      const res=await fetch("/api/import-recipe",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({model:"claude-haiku-4-5-20251001",max_tokens:1024,system:AI_IMPORT_SYSTEM,messages:[{role:"user",content:importText}]}),
      });
      if(!res.ok) throw new Error(`${res.status}`);
      const data=await res.json();
      const raw=data.content?.[0]?.text?.trim()||'';
      const p=JSON.parse(raw.replace(/^```(?:json)?\n?/,'').replace(/\n?```$/,''));
      const ings=p.ingredients||[];
      const hasMetricOrCountable=ings.some(i=>METRIC_UNITS.includes(i.unit)||(i.unit===''&&i.quantity!==''));
      if(p.blocked||(ings.length>0&&!hasMetricOrCountable)){
        setImportError("Only Metric/SI units are accepted (g, kg, ml, l). Please convert your recipe and try again.");
        return;
      }
      if(p.name) handleNameChange(p.name);
      if(ings.length){
        set('ingredients',ings.map((ing,i)=>({
          id:`i${Date.now()+i}`,
          name:ing.name||ing.item||ing.ingredient||'',
          qty:String(ing.quantity??ing.amount??''),
          unit:METRIC_UNITS.includes(ing.unit)?(ing.unit==='l'?'L':ing.unit):(ing.unit===''&&String(ing.quantity??ing.amount??'')!==''?'pcs':''),
          typeOverride:null,groupId:null,
        })));
      }
      const rawSteps=p.steps||p.method||[];
      const steps=Array.isArray(rawSteps)?rawSteps.map(s=>typeof s==='string'?s:s.instruction||s.text||s.description||'').filter(Boolean):[];
      if(steps.length) set('method',steps.join('\n'));
      set('method_grid',null);
      setImportOpen(false);setImportText('');
      showToast('Done, please review','success');
    }catch(err){
      console.error("Import failed:", err);
      setImportError("We couldn't read this recipe. Please check your paste and try again.");
    }finally{
      setImportLoading(false);
    }
  }

  async function handleImportPhoto(files){
    setImportLoading(true);setImportError('');
    try{
      const toBase64=f=>new Promise((resolve,reject)=>{
        const reader=new FileReader();
        reader.onload=()=>resolve(reader.result.split(',')[1]);
        reader.onerror=reject;
        reader.readAsDataURL(f);
      });
      const imageBlocks=await Promise.all(Array.from(files).map(async f=>({
        type:"image",source:{type:"base64",media_type:f.type||"image/jpeg",data:await toBase64(f)},
      })));
      const userText=files.length>1
        ?"These are multiple pages of a recipe. Combine all ingredients across all images into one list (no duplicates). Combine all method steps across all images into one steps list in order. Images showing ingredient lists go into ingredients; images showing instructions go into steps."
        :"Extract the recipe from this image.";
      const res=await fetch("/api/import-recipe",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          model:"claude-haiku-4-5-20251001",max_tokens:2048,system:AI_IMPORT_SYSTEM,
          messages:[{role:"user",content:[...imageBlocks,{type:"text",text:userText}]}],
        }),
      });
      if(!res.ok) throw new Error(`${res.status}`);
      const data=await res.json();
      const raw=data.content?.[0]?.text?.trim()||'';
      const p=JSON.parse(raw.replace(/^```(?:json)?\n?/,'').replace(/\n?```$/,''));
      const ings=p.ingredients||[];
      const hasMetricOrCountable=ings.some(i=>METRIC_UNITS.includes(i.unit)||(i.unit===''&&i.quantity!==''));
      if(p.blocked||(ings.length>0&&!hasMetricOrCountable)){
        setImportOpen(false);
        showToast('Import failed due to image quality','error');
        return;
      }
      if(p.name) handleNameChange(p.name);
      if(ings.length){
        set('ingredients',ings.map((ing,i)=>({
          id:`i${Date.now()+i}`,
          name:ing.name||ing.item||ing.ingredient||'',
          qty:String(ing.quantity??ing.amount??''),
          unit:METRIC_UNITS.includes(ing.unit)?(ing.unit==='l'?'L':ing.unit):(ing.unit===''&&String(ing.quantity??ing.amount??'')!==''?'pcs':''),
          typeOverride:null,groupId:null,
        })));
      }
      const rawSteps=p.steps||p.method||[];
      const steps=Array.isArray(rawSteps)?rawSteps.map(s=>typeof s==='string'?s:s.instruction||s.text||s.description||'').filter(Boolean):[];
      if(steps.length) set('method',steps.join('\n'));
      set('method_grid',null);
      setImportOpen(false);
      showToast('Done, please review','success');
    }catch(err){
      console.error("Photo import failed:",err);
      setImportOpen(false);
      showToast('Import failed due to image quality','error');
    }finally{
      setImportLoading(false);
      if(photoImportRef.current) photoImportRef.current.value='';
    }
  }

  function addGroup(){setR(p=>({...p,ingredients:[...p.ingredients,{id:Date.now().toString(),_group:true,name:""}]}));}

  function addIngAndFocus(gid){
    const newId=Date.now().toString();
    setR(p=>({...p,ingredients:[...p.ingredients,{id:newId,name:"",qty:"",unit:"g",typeOverride:null,groupId:gid||null}]}));
    setPendingFocusId(newId);
  }

  function setIng(id,k,v){setR(p=>({...p,ingredients:p.ingredients.map(i=>i.id===id?{...i,[k]:v}:i)}));}
  function delRow(id){setR(p=>({...p,ingredients:p.ingredients.filter(i=>i.id!==id)}));}
  function cycleType(id,name,cur){
    const order=["flour","liquid","other",null];
    setIng(id,"typeOverride",order[(order.indexOf(cur)+1)%order.length]);
  }
  function tColor(row){const t=row.typeOverride||guessType(row.name);return t==="flour"?C.wheat:t==="liquid"?"#07257F":C.border;}
  function tLabel(row){
    const ov=row.typeOverride;
    if(ov==="flour") return "F";if(ov==="liquid") return "L";if(ov==="other") return "—";
    const g=guessType(row.name);return g==="flour"?"F":g==="liquid"?"L":"·";
  }

  // Reorder within the flat ingredients array by swapping positions of the filtered subset
  function reorderFlat(arr, type, fromIdx, toIdx, groupId){
    if(fromIdx===toIdx) return arr;
    let predicate;
    if(type==='ungrouped') predicate=i=>!i._group&&!i.groupId;
    else if(type==='group') predicate=i=>!!i._group;
    else predicate=i=>!i._group&&i.groupId===groupId;

    const indices=[];
    arr.forEach((item,idx)=>{ if(predicate(item)) indices.push(idx); });
    if(fromIdx>=indices.length||toIdx>=indices.length) return arr;

    const result=[...arr];
    const ordered=indices.map(i=>arr[i]);
    const [removed]=ordered.splice(fromIdx,1);
    ordered.splice(toIdx,0,removed);
    indices.forEach((flatIdx,i)=>{ result[flatIdx]=ordered[i]; });
    return result;
  }

  function handleDragEnd(result){
    const {source,destination,type}=result;
    if(!destination) return;
    if(source.droppableId===destination.droppableId&&source.index===destination.index) return;

    if(type==='INGREDIENT'){
      setR(p=>({...p,ingredients:reorderFlat(p.ingredients,'ungrouped',source.index,destination.index)}));
    } else if(type==='GROUP'){
      setR(p=>({...p,ingredients:reorderFlat(p.ingredients,'group',source.index,destination.index)}));
    } else if(type==='MEMBER'){
      if(source.droppableId!==destination.droppableId) return;
      const gid=source.droppableId.replace('group-','');
      setR(p=>({...p,ingredients:reorderFlat(p.ingredients,'member',source.index,destination.index,gid)}));
    }
  }

  const iStyle={fontSize:12,padding:"6px 8px",border:`0.5px solid ${C.border}`,borderRadius:6,fontFamily:"Poppins,sans-serif",color:C.ink,background:C.white,outline:"none",width:"100%",boxSizing:"border-box"};
  const groups=r.ingredients.filter(i=>i._group);
  const ungrouped=r.ingredients.filter(i=>!i._group&&!i.groupId);

  function renderIng(ing, dragHandleProps){
    const pct=calcPct(ing,totals.weight,r.bake_type==='bread');

    function handleNameEnter(e){
      e.preventDefault();
      qtyRefs.current[ing.id]?.focus();
    }
    function handleQtyEnter(e){
      e.preventDefault();
      const section=r.ingredients.filter(i=>!i._group&&i.groupId===ing.groupId);
      const idx=section.findIndex(i=>i.id===ing.id);
      const next=section[idx+1];
      if(next&&nameRefs.current[next.id]){
        nameRefs.current[next.id].focus();
      } else {
        addIngAndFocus(ing.groupId);
      }
    }

    const isBread=r.bake_type==='bread';
    const needsQty=ing.qty==='';
    return(
      <div style={{display:"grid",gridTemplateColumns:isBread?"20px 2fr 1fr 0.8fr 0.6fr 22px 20px":"20px 2fr 1fr 0.8fr 0.6fr 20px",gap:4,marginBottom:7,alignItems:"center",borderLeft:needsQty?`2px solid #c0392b`:"2px solid transparent",paddingLeft:needsQty?4:4,borderRadius:3}}>
        <DragHandle dragHandleProps={dragHandleProps}/>
        <input
          ref={el=>{ nameRefs.current[ing.id]=el; }}
          value={ing.name} onChange={e=>setIng(ing.id,"name",e.target.value)}
          onKeyDown={e=>{ if(e.key==='Enter') handleNameEnter(e); }}
          placeholder="name" style={iStyle}
        />
        <input
          ref={el=>{ qtyRefs.current[ing.id]=el; }}
          value={ing.qty} onChange={e=>setIng(ing.id,"qty",e.target.value)}
          onKeyDown={e=>{ if(e.key==='Enter') handleQtyEnter(e); }}
          placeholder="qty" style={{...iStyle,borderColor:needsQty?'#c0392b':C.border}}
        />
        <select value={ing.unit} onChange={e=>setIng(ing.id,"unit",e.target.value)}
          style={{fontSize:11,padding:"6px 3px",border:`0.5px solid ${C.border}`,borderRadius:6,fontFamily:"Poppins,sans-serif",color:C.ink,background:C.white,outline:"none",appearance:"none",width:"100%"}}>
          {UNITS.map(u=><option key={u}>{u}</option>)}
        </select>
        <span style={{fontSize:11,color:C.muted,fontFamily:"Poppins,sans-serif",textAlign:"center"}}>{pct}</span>
        {isBread&&(
          <button onClick={()=>cycleType(ing.id,ing.name,ing.typeOverride)}
            style={{background:"none",border:`0.5px solid ${tColor(ing)}`,borderRadius:4,cursor:"pointer",color:tColor(ing),fontSize:9,padding:0,fontWeight:600,width:22,height:22,textAlign:"center"}}>
            {tLabel(ing)}
          </button>
        )}
        <button onClick={()=>delRow(ing.id)} style={{background:"none",border:"none",cursor:"pointer",color:C.muted,fontSize:15,padding:0,width:20,textAlign:"center"}}>×</button>
      </div>
    );
  }

  return(
    <div style={{fontFamily:"Poppins,sans-serif"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <button onClick={onCancel} style={{background:"none",border:"none",cursor:"pointer",color:C.muted,fontSize:11,padding:0,letterSpacing:"0.06em",textTransform:"uppercase"}}>← back</button>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          {r.id&&<button onClick={()=>setExportOpen(true)} style={{background:"none",border:`0.5px solid ${C.border}`,borderRadius:7,cursor:"pointer",color:C.muted,fontSize:10,padding:"5px 10px",letterSpacing:"0.06em",textTransform:"uppercase",fontFamily:"Poppins,sans-serif"}}>export</button>}
          <button onClick={()=>set("is_favourite",!r.is_favourite)} style={{background:"none",border:"none",cursor:"pointer",fontSize:18,padding:0}}>{r.is_favourite?"★":"☆"}</button>
        </div>
      </div>
      <button onClick={()=>setImportOpen(true)} style={{width:"100%",padding:"9px 0",marginBottom:16,border:`0.5px solid ${C.border}`,borderRadius:8,background:"none",color:C.muted,fontSize:10,cursor:"pointer",fontFamily:"Poppins,sans-serif",letterSpacing:"0.06em",textTransform:"uppercase"}}>import</button>
      <div style={{marginBottom:14}}><Label>recipe name</Label><Inp value={r.name} onChange={handleNameChange} placeholder="e.g. Classic Sourdough"/></div>
      <div style={{marginBottom:14}}>
        <div style={{display:"flex",alignItems:"baseline",gap:4,margin:"0 0 6px"}}>
          <p style={{fontSize:10,letterSpacing:"0.06em",textTransform:"uppercase",color:C.muted,margin:0,fontFamily:"Poppins,sans-serif"}}>Label</p>
          <p style={{fontSize:8,color:C.muted,margin:0,fontFamily:"Poppins,sans-serif",fontStyle:"italic"}}>(Auto)</p>
        </div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {BAKE_TYPES.map(t=>(
            <div key={t.id} onClick={()=>{set("bake_type",t.id);setTypeOverridden(true);}}
              style={{padding:"5px 11px",borderRadius:20,border:`0.5px solid ${r.bake_type===t.id?C.wheat:C.border}`,background:"none",cursor:"pointer"}}>
              <span style={{fontSize:10,letterSpacing:"0.06em",textTransform:"uppercase",color:r.bake_type===t.id?C.wheat:C.muted}}>{t.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{marginBottom:14}}>
        <Label>category</Label>
        <select value={r.category} onChange={e=>set("category",e.target.value)}
          style={{width:"100%",fontSize:13,padding:"9px 11px",border:`0.5px solid ${C.border}`,borderRadius:8,fontFamily:"Poppins,sans-serif",color:r.category?C.ink:C.muted,background:C.white,outline:"none",appearance:"none",fontStyle:r.category?'normal':'italic'}}>
          <option value="" disabled>Please select category</option>
          {[...categories].sort().map(c=><option key={c} style={{fontStyle:'normal',color:C.ink}}>{c}</option>)}
        </select>
      </div>

      <TotalsBar rows={r.ingredients} bakeType={r.bake_type}/>
      {hasGrid&&(
        <div style={{display:"flex",gap:6,marginBottom:14}}>
          <SmBtn active onClick={()=>setGridOpen(true)}>view grid</SmBtn>
        </div>
      )}
      <div style={{marginBottom:14}}>
        <Label>ingredients</Label>
        <div style={{display:"grid",gridTemplateColumns:r.bake_type==='bread'?"20px 2fr 1fr 0.8fr 0.6fr 22px 20px":"20px 2fr 1fr 0.8fr 0.6fr 20px",gap:4,marginBottom:6}}>
          {(r.bake_type==='bread'?["","name","qty","unit","%","type",""]:["","name","qty","unit","%",""]).map((h,i)=>(
            <span key={i} style={{fontSize:9,letterSpacing:"0.06em",textTransform:"uppercase",color:C.muted,fontFamily:"Poppins,sans-serif"}}>{h}</span>
          ))}
        </div>

        <DragDropContext onDragEnd={handleDragEnd}>
          {/* Ungrouped ingredients */}
          <Droppable droppableId="ungrouped" type="INGREDIENT">
            {(prov)=>(
              <div ref={prov.innerRef} {...prov.droppableProps}>
                {ungrouped.map((ing,index)=>(
                  <Draggable key={ing.id} draggableId={ing.id} index={index}>
                    {(itemProv)=>(
                      <div
                        ref={itemProv.innerRef}
                        {...itemProv.draggableProps}
                        style={{...itemProv.draggableProps.style}}
                      >
                        {renderIng(ing, itemProv.dragHandleProps)}
                      </div>
                    )}
                  </Draggable>
                ))}
                {prov.placeholder}
              </div>
            )}
          </Droppable>

          {/* Group blocks */}
          <Droppable droppableId="groups" type="GROUP">
            {(groupsProv)=>(
              <div ref={groupsProv.innerRef} {...groupsProv.droppableProps}>
                {groups.map((grp,gIndex)=>{
                  const members=r.ingredients.filter(i=>!i._group&&i.groupId===grp.id);
                  return(
                    <Draggable key={grp.id} draggableId={grp.id} index={gIndex}>
                      {(grpProv)=>(
                        <div
                          ref={grpProv.innerRef}
                          {...grpProv.draggableProps}
                          style={{marginBottom:8,...grpProv.draggableProps.style}}
                        >
                          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                            <DragHandle dragHandleProps={grpProv.dragHandleProps} color={C.sand}/>
                            <input value={grp.name} onChange={e=>setIng(grp.id,"name",e.target.value)} placeholder="Group name"
                              style={{fontSize:11,padding:"4px 8px",border:`0.5px solid ${C.sand}`,borderRadius:6,fontFamily:"Poppins,sans-serif",color:C.muted,background:C.sandLight,outline:"none",flex:1}}/>
                            <div style={{flex:1,height:0.5,background:C.border}}/>
                            <button onClick={()=>delRow(grp.id)} style={{background:"none",border:"none",cursor:"pointer",color:C.muted,fontSize:15,padding:0,width:20,textAlign:"center"}}>×</button>
                          </div>
                          <div style={{paddingLeft:12,borderLeft:`1.5px solid ${C.border}`}}>
                            <Droppable droppableId={`group-${grp.id}`} type="MEMBER">
                              {(memProv)=>(
                                <div ref={memProv.innerRef} {...memProv.droppableProps}>
                                  {members.map((ing,mIdx)=>(
                                    <Draggable key={ing.id} draggableId={ing.id} index={mIdx}>
                                      {(memItemProv)=>(
                                        <div
                                          ref={memItemProv.innerRef}
                                          {...memItemProv.draggableProps}
                                          style={{...memItemProv.draggableProps.style}}
                                        >
                                          {renderIng(ing, memItemProv.dragHandleProps)}
                                        </div>
                                      )}
                                    </Draggable>
                                  ))}
                                  {memProv.placeholder}
                                </div>
                              )}
                            </Droppable>
                            <span onClick={()=>addIngAndFocus(grp.id)} style={{fontSize:10,letterSpacing:"0.06em",textTransform:"uppercase",color:C.muted,cursor:"pointer"}}>+ ingredient</span>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {groupsProv.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {r.bake_type==='bread'&&(
          <p style={{fontSize:10,color:C.muted,fontStyle:"italic",margin:"4px 0 0",fontFamily:"Poppins,sans-serif"}}>Refer to Hydration % for liquid ingredients</p>
        )}

        <div style={{display:"flex",gap:12,marginTop:8}}>
          <span onClick={()=>addIngAndFocus(null)} style={{fontSize:10,letterSpacing:"0.06em",textTransform:"uppercase",color:C.wheat,cursor:"pointer"}}>+ ingredient</span>
          <span onClick={addGroup} style={{fontSize:10,letterSpacing:"0.06em",textTransform:"uppercase",color:C.sand,cursor:"pointer"}}>+ group</span>
        </div>
      </div>

      <div style={{marginBottom:20}}>
        <Label>method notes</Label>
        <Tarea value={r.method} onChange={v=>set("method",v)} placeholder="Steps, timings, temperatures…"/>
        <div style={{display:"flex",gap:12,marginTop:8,alignItems:"center",flexWrap:"wrap"}}>
          <span onClick={()=>{if(!gridLoading&&canBuildGrid) handleBuildGrid();}}
            style={{fontSize:10,letterSpacing:"0.06em",textTransform:"uppercase",color:C.wheat,cursor:canBuildGrid&&!gridLoading?"pointer":"default",opacity:canBuildGrid&&!gridLoading?1:0.4}}>
            {r.method_grid?"rebuild grid":"build grid"}
          </span>
          {r.method_grid&&<span onClick={()=>set("method_grid",null)}
            style={{fontSize:10,letterSpacing:"0.06em",textTransform:"uppercase",color:C.muted,cursor:"pointer"}}>clear grid</span>}
          {gridLoading&&<span style={{fontSize:11,color:C.muted,fontStyle:"italic"}}>Working out the grid…</span>}
        </div>
        {!canBuildGrid&&!r.method_grid&&<p style={{fontSize:10,color:C.muted,fontStyle:"italic",margin:"6px 0 0"}}>Add ingredients and method notes to build a grid.</p>}
      </div>
      <div style={{borderTop:`0.5px solid ${C.border}`,paddingTop:20,marginBottom:20}}>
        <PhotoStrip
          photos={photos}
          onAdd={handleAddPhotos}
          onDelete={handleDeletePhoto}
          onCaption={handleCaptionSave}
          onReplace={handleReplacePhoto}
          uploading={photoUploading}
        />
      </div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        <Btn onClick={onCancel}>cancel</Btn>
        {onDelete&&<Btn danger onClick={()=>onDelete(r.id)}>delete</Btn>}
        <Btn primary onClick={()=>onSave(r,photos.filter(p=>p.pending))} disabled={!r.name.trim()||!r.category}>save</Btn>
      </div>
      {deleteMsg&&<p style={{fontSize:11,color:"#c0392b",margin:"10px 0 0",fontFamily:"Poppins,sans-serif"}}>{deleteMsg}</p>}
      {toast.msg&&(
        <div style={{position:"fixed",bottom:28,left:"50%",transform:"translateX(-50%)",zIndex:200,pointerEvents:"none"}}>
          <div style={{background:toast.type==='success'?C.ink:"#c0392b",color:"#fff",borderRadius:20,padding:"10px 20px",fontSize:12,fontFamily:"Poppins,sans-serif",letterSpacing:"0.03em",whiteSpace:"nowrap",boxShadow:"0 4px 16px rgba(0,0,0,0.18)"}}>
            {toast.msg}
          </div>
        </div>
      )}
      {gridOpen&&hasGrid&&<GridModal recipe={r} editable onChange={v=>set("method_grid",v)} onExport={()=>setGridExportOpen(true)} onClose={()=>setGridOpen(false)}/>}
      {gridExportOpen&&hasGrid&&<ExportModal recipe={r} adj={{}} grid filename={`${(r.name||'recipe').replace(/[^a-z0-9]/gi,'_').toLowerCase()}_grid`} onClose={()=>setGridExportOpen(false)}/>}
      {exportOpen&&<ExportModal recipe={r} adj={{}} filename={(r.name||'recipe').replace(/[^a-z0-9]/gi,'_').toLowerCase()} onClose={()=>setExportOpen(false)}/>}
      {importOpen&&(
        <div style={{position:"fixed",inset:0,background:"rgba(13,13,13,0.4)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:100,padding:"0 16px",boxSizing:"border-box"}} onClick={()=>setImportOpen(false)}>
          <style>{`.import-ta::placeholder{font-style:italic}`}</style>
          <div
            onClick={e=>e.stopPropagation()}
            onPaste={e=>{
              const item=Array.from(e.clipboardData.items).find(i=>i.type.startsWith('image/'));
              if(item){e.preventDefault();const f=item.getAsFile();if(f) handleImportPhoto([f]);}
            }}
            style={{background:C.parchment,borderRadius:16,padding:"20px 20px 28px",width:"100%",maxWidth:420,boxSizing:"border-box"}}
          >
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <p style={{fontSize:11,letterSpacing:"0.06em",textTransform:"uppercase",color:C.muted,margin:0,fontFamily:"Poppins,sans-serif"}}>paste from AI</p>
              <button onClick={()=>photoImportRef.current&&photoImportRef.current.click()} disabled={importLoading}
                style={{padding:"9px 14px",border:"none",borderRadius:8,background:C.wheat,color:"#fff",fontSize:11,cursor:"pointer",fontFamily:"Poppins,sans-serif",letterSpacing:"0.04em",opacity:importLoading?0.4:1}}>
                import via photo
              </button>
              <input ref={photoImportRef} type="file" accept="image/*" multiple style={{display:"none"}}
                onChange={e=>{const fs=e.target.files;if(fs&&fs.length) handleImportPhoto(fs);}}/>
            </div>
            <Tarea className="import-ta" value={importText} onChange={v=>{setImportText(v);setImportError('');}} placeholder="paste your recipe here." rows={8}/>
            {importLoading&&<p style={{fontSize:11,color:C.muted,margin:"8px 0 0",fontFamily:"Poppins,sans-serif",fontStyle:"italic"}}>Reading your recipe…</p>}
            {importError&&<p style={{fontSize:11,color:"#c0392b",margin:"8px 0 0",fontFamily:"Poppins,sans-serif"}}>{importError}</p>}
            <div style={{display:"flex",gap:8,marginTop:12}}>
              <Btn onClick={()=>{setImportOpen(false);setImportText('');setImportError('');}} disabled={importLoading}>cancel</Btn>
              <Btn primary onClick={handleImport} disabled={!importText.trim()||importLoading}>import</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RecipeTab({recipes,categories,onSave,onDelete,search,onSearchChange,catFilter,onCatFilterChange,sort,onSortChange,uploadPhoto,deletePhoto,updatePhotoCaption}){
  const [editing,setEditing]=useState(null);
  const [expanded,setExpanded]=useState({});
  const [deleteMsg,setDeleteMsg]=useState('');
  const toggle=id=>setExpanded(p=>({...p,[id]:!p[id]}));

  async function handleSave(r,pendingPhotos){const ok=await onSave(r,pendingPhotos);if(ok!==false){setEditing(null);}}
  function handleDelete(id){
    const hasVariants=recipes.some(v=>v.parent_id===id);
    if(hasVariants){setDeleteMsg("Cannot be deleted — at least one variant exists.");return;}
    setDeleteMsg('');
    onDelete(id);
    setEditing(null);
  }
  function dup(r){onSave({...r,id:"",name:r.name+" (copy)",is_favourite:false,parent_id:r.parent_id||r.id},[]);}

  const photoProps={uploadPhoto,deletePhoto,updatePhotoCaption};

  if(editing) return <RecipeEditor recipe={editing} categories={categories} onSave={handleSave} onCancel={()=>{setEditing(null);setDeleteMsg('');}} onDelete={handleDelete} deleteMsg={deleteMsg} {...photoProps}/>;

  const masters=recipes.filter(r=>!r.parent_id);
  const allVars=recipes.filter(r=>!!r.parent_id);

  const filteredMasters=masters.filter(r=>{
    if(catFilter!=="all"&&(r.category||"Uncategorised")!==catFilter) return false;
    if(search.trim()&&!r.name.toLowerCase().includes(search.trim().toLowerCase())) return false;
    return true;
  });
  const sortedMasters=[...filteredMasters].sort((a,b)=>sort==="za"?b.name.localeCompare(a.name):a.name.localeCompare(b.name));

  const byCat={};
  sortedMasters.forEach(r=>{const k=r.category||"Uncategorised";if(!byCat[k]) byCat[k]=[];byCat[k].push(r);});

  return(
    <div style={{fontFamily:"Poppins,sans-serif"}}>
      <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
        <input value={search} onChange={e=>onSearchChange(e.target.value)} placeholder="search by name"
          style={{width:"100%",boxSizing:"border-box",padding:"8px 12px",border:`0.5px solid ${C.border}`,borderRadius:8,fontFamily:"Poppins,sans-serif",fontSize:12,color:C.ink,background:C.white,outline:"none"}}/>
        <div style={{display:"flex",gap:8}}>
          <select value={catFilter} onChange={e=>onCatFilterChange(e.target.value)}
            style={{flex:1,padding:"7px 8px",border:`0.5px solid ${C.border}`,borderRadius:8,fontFamily:"Poppins,sans-serif",fontSize:11,color:C.ink,background:C.white,outline:"none"}}>
            <option value="all">all categories</option>
            {categories.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          <select value={sort} onChange={e=>onSortChange(e.target.value)}
            style={{flex:1,padding:"7px 8px",border:`0.5px solid ${C.border}`,borderRadius:8,fontFamily:"Poppins,sans-serif",fontSize:11,color:C.ink,background:C.white,outline:"none"}}>
            <option value="az">name A–Z</option>
            <option value="za">name Z–A</option>
          </select>
        </div>
      </div>
      {sortedMasters.length===0
        ?<p style={{fontSize:13,color:C.muted,textAlign:"center",marginTop:24}}>{masters.length===0?"No recipes yet.":"No recipes found."}</p>
        :Object.keys(byCat).sort().map(cat=>(
          <div key={cat} style={{marginBottom:24}}>
            <p style={{fontSize:10,letterSpacing:"0.06em",textTransform:"uppercase",color:C.muted,margin:"0 0 10px"}}>{cat}</p>
            {byCat[cat].map(master=>{
              const vars=allVars.filter(v=>v.parent_id===master.id);
              const isOpen=!!expanded[master.id];
              const hasVars=vars.length>0;
              return(
                <div key={master.id} style={{marginBottom:12}}>
                  <div style={{background:C.white,border:`0.5px solid ${C.border}`,borderRadius:hasVars&&isOpen?"12px 12px 0 0":12,padding:"13px 15px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
                      <div style={{flex:1,cursor:"pointer",minWidth:0}} onClick={()=>setEditing(master)}>
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          {master.is_favourite&&<span style={{color:C.wheat,fontSize:13,flexShrink:0}}>★</span>}
                          <p style={{margin:0,fontSize:14,fontWeight:300,color:C.ink,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{master.name}</p>
                        </div>
                        <p style={{margin:"3px 0 0",fontSize:11,color:C.muted}}>
                          {(master.ingredients||[]).filter(i=>!i._group).length} ingredients
                          {hasVars?` · ${vars.length} variation${vars.length!==1?"s":""}` :""}
                        </p>
                      </div>
                      <div style={{display:"flex",gap:6,flexShrink:0}}>
                        {hasVars&&<SmBtn active={isOpen} onClick={()=>toggle(master.id)}>{isOpen?"collapse":"expand"}</SmBtn>}
                        <SmBtn onClick={()=>dup(master)}>duplicate</SmBtn>
                      </div>
                    </div>
                  </div>
                  {hasVars&&isOpen&&(
                    <div style={{border:`0.5px solid ${C.border}`,borderTop:"none",borderRadius:"0 0 12px 12px",overflow:"hidden"}}>
                      {vars.map((v,vi)=>{
                        return(
                          <div key={v.id} style={{background:vi%2===0?C.parchment:C.white,borderTop:`0.5px solid ${C.border}`,padding:"11px 14px",display:"flex",alignItems:"center",gap:8}}>
                            <div style={{width:2,height:28,background:C.border,borderRadius:1,flexShrink:0}}/>
                            <div style={{flex:1,cursor:"pointer",minWidth:0}} onClick={()=>setEditing(v)}>
                              <div style={{display:"flex",alignItems:"center",gap:5}}>
                                {v.is_favourite&&<span style={{color:C.wheat,fontSize:11,flexShrink:0}}>★</span>}
                                <p style={{margin:0,fontSize:13,fontWeight:300,color:C.ink,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{v.name}</p>
                              </div>
                              {v.variation_name&&<p style={{margin:"2px 0 0",fontSize:10,color:C.wheat}}>{v.variation_name}</p>}
                            </div>
                            <SmBtn onClick={()=>dup(v)}>duplicate</SmBtn>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))
      }
    </div>
  );
}

// --- LogForm ---
function LogForm({data,bake,onSave,onCancel,onSaveVariant,uploadPhoto,deletePhoto,updatePhotoCaption}){
  const empty={id:"",name:"",category:"",bake_type:"non-bread",recipe_id:"",adjusted_ingredients:{},notes:"",scores:{taste:3,look:3},date:today()};
  const [form,setForm]=useState(bake?{...empty,...bake,scores:normalizeScores(bake.scores),adjusted_ingredients:bake.adjusted_ingredients||{}}:empty);
  const [step,setStep]=useState(0);
  const [photos,setPhotos]=useState([]);
  const [photoUploading,setPhotoUploading]=useState(false);
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const setAdj=(id,v)=>setForm(f=>({...f,adjusted_ingredients:{...f.adjusted_ingredients,[id]:v}}));
  const chosenRecipe=data.recipes.find(r=>r.id===form.recipe_id);
  const steps=chosenRecipe?["recipe","view","notes"]:["recipe","notes"];

  // --- Scaling state (new log only; editing an existing bake leaves this off) ---
  const [scaleOn,setScaleOn]=useState(false);
  const [scaleMethod,setScaleMethod]=useState('multiplier');
  const [multPreset,setMultPreset]=useState('');
  const [multCustom,setMultCustom]=useState('');
  const [anchorId,setAnchorId]=useState('');
  const [anchorQty,setAnchorQty]=useState('');
  const [variantPrompt,setVariantPrompt]=useState(false);
  const [pendingSave,setPendingSave]=useState(null);

  function applyScaleToForm(ratio){
    if(!chosenRecipe||!ratio) return;
    const adj={};
    (chosenRecipe.ingredients||[]).filter(i=>!i._group).forEach(i=>{
      const q=parseFloat(i.qty);
      if(q&&q>0){const s=q*ratio;const fl=Math.floor(s);adj[i.id]=String(s-fl>=0.4?fl+1:fl);}
    });
    setForm(f=>({...f,adjusted_ingredients:adj}));
  }
  function getEffectiveRatio(){
    if(!scaleOn||!chosenRecipe) return null;
    if(scaleMethod==='multiplier'){
      const m=parseFloat(multPreset||multCustom);
      return(m&&m>0)?m:null;
    }
    if(scaleMethod==='anchor'&&anchorId&&anchorQty){
      const a=(chosenRecipe.ingredients||[]).filter(i=>!i._group).find(i=>i.id===anchorId);
      if(!a) return null;
      const oq=parseFloat(a.qty),tq=parseFloat(anchorQty);
      return(oq>0&&tq>0)?tq/oq:null;
    }
    return null;
  }
  function handleScaleToggle(on){
    setScaleOn(on);
    if(!on){setMultPreset('');setMultCustom('');setAnchorId('');setAnchorQty('');setForm(f=>({...f,adjusted_ingredients:{}}));}
  }
  function handleAnchorIdChange(id){setAnchorId(id);setAnchorQty('');setForm(f=>({...f,adjusted_ingredients:{}}));}
  function handleAnchorQtyChange(val){
    setAnchorQty(val);
    if(!anchorId||!chosenRecipe) return;
    const a=(chosenRecipe.ingredients||[]).filter(i=>!i._group).find(i=>i.id===anchorId);
    if(!a) return;
    const oq=parseFloat(a.qty),tq=parseFloat(val);
    if(oq>0&&tq>0) applyScaleToForm(tq/oq);
    else setForm(f=>({...f,adjusted_ingredients:{}}));
  }
  const effectiveRatio=getEffectiveRatio();
  const variantNameStr=chosenRecipe
    ?(effectiveRatio?`${chosenRecipe.name} × ${parseFloat(effectiveRatio.toFixed(2))}`:`${chosenRecipe.name} (scaled)`)
    :'';
  function handleVariantYes(){
    const scaledIngs=(chosenRecipe.ingredients||[]).map(i=>{
      if(i._group) return i;
      const adjQty=form.adjusted_ingredients[i.id];
      return adjQty!==undefined?{...i,qty:adjQty}:i;
    });
    const variantRecipe={
      ...chosenRecipe,id:'',name:variantNameStr,parent_id:chosenRecipe.parent_id||chosenRecipe.id,
      variation_name:effectiveRatio?`× ${parseFloat(effectiveRatio.toFixed(2))}`:'scaled',
      ingredients:scaledIngs,
    };
    if(onSaveVariant) onSaveVariant(variantRecipe,[]);
    setVariantPrompt(false);
    onSave(form,pendingSave||[]);
    setPendingSave(null);
  }
  function handleVariantNo(){
    setVariantPrompt(false);
    onSave(form,pendingSave||[]);
    setPendingSave(null);
  }

  useEffect(()=>{
    if(bake?.id) loadPhotosForEntity('bake',bake.id).then(setPhotos);
  },[bake?.id]);// eslint-disable-line

  async function handleAddPhotos(files){
    if(bake?.id){
      setPhotoUploading(true);
      for(const file of files){
        const photo=await uploadPhoto('bake',bake.id,file,'');
        if(photo) setPhotos(prev=>[...prev,photo]);
      }
      setPhotoUploading(false);
    } else {
      const newPhotos=files.map(file=>({
        id:`pending-${Date.now()}-${Math.random()}`,
        url:URL.createObjectURL(file),
        caption:'',file,pending:true,
      }));
      setPhotos(prev=>[...prev,...newPhotos]);
    }
  }

  async function handleDeletePhoto(id){
    const photo=photos.find(p=>p.id===id);
    if(!photo) return;
    if(photo.pending){URL.revokeObjectURL(photo.url);}
    else{await deletePhoto(photo);}
    setPhotos(prev=>prev.filter(p=>p.id!==id));
  }

  async function handleCaptionSave(id,caption){
    setPhotos(prev=>prev.map(p=>p.id===id?{...p,caption}:p));
    const photo=photos.find(p=>p.id===id);
    if(photo&&!photo.pending) await updatePhotoCaption(id,caption);
  }

  async function handleReplacePhoto(id,newFile){
    await handleDeletePhoto(id);
    await handleAddPhotos([newFile]);
  }

  function renderAdjIng(ing){
    const a=form.adjusted_ingredients[ing.id];
    const isAdj=a!==undefined&&a!==ing.qty;
    return(
      <div key={ing.id} style={{display:"grid",gridTemplateColumns:"2fr 1fr 0.7fr",gap:4,marginBottom:8,alignItems:"center"}}>
        <span style={{fontSize:13,color:C.ink,fontFamily:"Poppins,sans-serif"}}>{ing.name}</span>
        <input value={a!==undefined?a:ing.qty} onChange={e=>setAdj(ing.id,e.target.value)}
          style={{fontSize:13,padding:"6px 8px",border:`0.5px solid ${isAdj?C.wheat:C.border}`,borderRadius:6,fontFamily:"Poppins,sans-serif",color:isAdj?C.wheat:C.ink,background:isAdj?C.wheatLight:C.white,fontWeight:isAdj?500:400,outline:"none",width:"100%",boxSizing:"border-box"}}/>
        <span style={{fontSize:12,color:C.muted,fontFamily:"Poppins,sans-serif"}}>{ing.unit}</span>
      </div>
    );
  }

  const stepLabels=chosenRecipe?["01 — recipe","02 — recipe view","03 — notes"]:["01 — recipe","02 — notes"];

  return(
    <div style={{fontFamily:"Poppins,sans-serif"}}>
      <div style={{display:"flex",gap:5,marginBottom:20}}>
        {steps.map((_,i)=><div key={i} style={{flex:1,height:2,borderRadius:1,background:i<=step?C.wheat:C.border}}/>)}
      </div>
      <Label>{stepLabels[step]}</Label>
      {step===0&&(
        <div>
          <div style={{marginBottom:14}}>
            <Label>select recipe</Label>
            <select value={form.recipe_id} onChange={e=>{
              const rid=e.target.value;
              const rec=data.recipes.find(r=>r.id===rid);
              setScaleOn(false);setMultPreset('');setMultCustom('');setAnchorId('');setAnchorQty('');
              setForm(f=>({...f,recipe_id:rid,name:rec?rec.name:f.name,category:rec&&rec.category?rec.category:f.category,adjusted_ingredients:{}}));
            }} style={{width:"100%",fontSize:13,padding:"9px 11px",border:`0.5px solid ${C.border}`,borderRadius:8,fontFamily:"Poppins,sans-serif",color:C.ink,background:C.white,outline:"none",appearance:"none",marginBottom:10}}>
              <option value="">— no recipe —</option>
              {data.recipes.map(r=><option key={r.id} value={r.id}>{r.name}{r.variation_name?` — ${r.variation_name}`:""}</option>)}
            </select>
          </div>
          {/* Scaling toggle — only shown for new bake logs when a recipe is selected */}
          {chosenRecipe&&!bake&&(
            <div style={{marginBottom:14,padding:"12px 14px",border:`0.5px solid ${scaleOn?C.wheat:C.border}`,borderRadius:10,background:scaleOn?C.wheatLight:C.white}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer"}} onClick={()=>handleScaleToggle(!scaleOn)}>
                <span style={{fontSize:12,color:C.ink,fontFamily:"Poppins,sans-serif"}}>Scale this recipe?</span>
                <div style={{width:36,height:20,borderRadius:10,border:`0.5px solid ${scaleOn?C.wheat:C.border}`,background:scaleOn?C.wheat:C.white,position:"relative",flexShrink:0}}>
                  <div style={{width:14,height:14,borderRadius:7,background:scaleOn?"#fff":C.border,position:"absolute",top:3,left:scaleOn?19:3,transition:"left 0.15s"}}/>
                </div>
              </div>
              {scaleOn&&(
                <div style={{marginTop:14}}>
                  {/* Method tabs */}
                  <div style={{display:"flex",gap:6,marginBottom:12}}>
                    {[{id:'multiplier',label:'Multiplier'},{id:'anchor',label:'Anchor ingredient'}].map(m=>(
                      <button key={m.id}
                        onClick={()=>{setScaleMethod(m.id);setMultPreset('');setMultCustom('');setAnchorId('');setAnchorQty('');setForm(f=>({...f,adjusted_ingredients:{}}));}}
                        style={{padding:"5px 10px",borderRadius:6,border:`0.5px solid ${scaleMethod===m.id?C.wheat:C.border}`,background:scaleMethod===m.id?C.wheat:"none",color:scaleMethod===m.id?"#fff":C.muted,fontSize:10,cursor:"pointer",fontFamily:"Poppins,sans-serif",letterSpacing:"0.04em"}}>
                        {m.label}
                      </button>
                    ))}
                  </div>

                  {scaleMethod==='multiplier'&&(
                    <div>
                      <div style={{display:"flex",gap:6,marginBottom:10}}>
                        {['1.5','2','2.5'].map(v=>(
                          <button key={v}
                            onClick={()=>{setMultPreset(v);setMultCustom('');applyScaleToForm(parseFloat(v));}}
                            style={{flex:1,padding:"9px 0",borderRadius:7,border:`0.5px solid ${multPreset===v?C.wheat:C.border}`,background:multPreset===v?C.wheat:"none",color:multPreset===v?"#fff":C.muted,fontSize:13,cursor:"pointer",fontFamily:"Poppins,sans-serif",fontWeight:multPreset===v?500:300}}>
                            {v}×
                          </button>
                        ))}
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <span style={{fontSize:11,color:C.muted,fontFamily:"Poppins,sans-serif",whiteSpace:"nowrap"}}>custom ×</span>
                        <input value={multCustom} placeholder="e.g. 3"
                          onChange={e=>{const v=e.target.value;setMultCustom(v);setMultPreset('');const m=parseFloat(v);if(m&&m>0) applyScaleToForm(m);else setForm(f=>({...f,adjusted_ingredients:{}}));}}
                          style={{flex:1,fontSize:12,padding:"7px 9px",border:`0.5px solid ${C.border}`,borderRadius:7,fontFamily:"Poppins,sans-serif",color:C.ink,background:C.white,outline:"none"}}/>
                      </div>
                    </div>
                  )}

                  {scaleMethod==='anchor'&&(
                    <div>
                      <div style={{marginBottom:8}}>
                        <Label>select ingredient</Label>
                        <select value={anchorId} onChange={e=>handleAnchorIdChange(e.target.value)}
                          style={{width:"100%",fontSize:12,padding:"7px 10px",border:`0.5px solid ${C.border}`,borderRadius:7,fontFamily:"Poppins,sans-serif",color:anchorId?C.ink:C.muted,background:C.white,outline:"none",appearance:"none",fontStyle:anchorId?'normal':'italic'}}>
                          <option value="">— select ingredient —</option>
                          {(chosenRecipe.ingredients||[]).filter(i=>!i._group&&i.qty).map(i=>(
                            <option key={i.id} value={i.id}>{i.name}{i.qty?` (${i.qty}${i.unit||''})`:''}</option>
                          ))}
                        </select>
                      </div>
                      {anchorId&&(
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <span style={{fontSize:11,color:C.muted,fontFamily:"Poppins,sans-serif"}}>I have</span>
                          <input value={anchorQty} placeholder="amount"
                            onChange={e=>handleAnchorQtyChange(e.target.value)}
                            style={{flex:1,fontSize:12,padding:"7px 9px",border:`0.5px solid ${C.border}`,borderRadius:7,fontFamily:"Poppins,sans-serif",color:C.ink,background:C.white,outline:"none"}}/>
                          <span style={{fontSize:12,color:C.muted,fontFamily:"Poppins,sans-serif"}}>
                            {(chosenRecipe.ingredients||[]).filter(i=>!i._group).find(i=>i.id===anchorId)?.unit||''}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Method 3 — Target total weight: not built yet.
                      TODO: let user type a desired total dough weight; derive ratio = targetWeight / currentTotalWeight; apply to all ingredients. */}

                  {effectiveRatio&&(
                    <p style={{fontSize:10,color:C.wheat,margin:"10px 0 0",fontFamily:"Poppins,sans-serif",fontStyle:"italic"}}>
                      ×{parseFloat(effectiveRatio.toFixed(2))} — scaled amounts will show in recipe view
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          <div style={{marginBottom:14}}><Label>journal name</Label><Inp value={form.name} onChange={v=>set("name",v)} placeholder="Name this bake…"/></div>
          <div style={{marginBottom:14}}>
            <Label>category</Label>
            <select value={form.category} onChange={e=>set("category",e.target.value)}
              style={{width:"100%",fontSize:13,padding:"9px 11px",border:`0.5px solid ${C.border}`,borderRadius:8,fontFamily:"Poppins,sans-serif",color:form.category?C.ink:C.muted,background:C.white,outline:"none",appearance:"none",fontStyle:form.category?'normal':'italic'}}>
              <option value="" disabled>Please select category</option>
              {[...data.categories].sort().map(c=><option key={c} style={{fontStyle:'normal',color:C.ink}}>{c}</option>)}
            </select>
          </div>
          <div style={{marginBottom:14}}>
            <Label>date</Label>
            <input type="date" value={form.date} onChange={e=>set("date",e.target.value)}
              style={{width:"100%",boxSizing:"border-box",fontSize:13,padding:"9px 11px",border:`0.5px solid ${C.border}`,borderRadius:8,fontFamily:"Poppins,sans-serif",color:C.ink,background:C.white,outline:"none"}}/>
            <p style={{fontSize:11,color:C.muted,margin:"4px 0 0"}}>{fmtDate(form.date)}</p>
          </div>
        </div>
      )}
      {step===1&&chosenRecipe&&(()=>{
        const ings=chosenRecipe.ingredients||[];
        const adjRows=ings.map(i=>({...i,qty:form.adjusted_ingredients[i.id]!==undefined?form.adjusted_ingredients[i.id]:i.qty}));
        const groups=ings.filter(i=>i._group);
        const ungrouped=ings.filter(i=>!i._group&&!i.groupId);
        return(
          <div>
            <p style={{fontSize:14,fontWeight:300,color:C.ink,margin:"0 0 4px"}}>{chosenRecipe.name}</p>
            <p style={{fontSize:11,color:C.muted,margin:"0 0 12px"}}>{scaleOn&&effectiveRatio?'Showing scaled amounts. Tap to fine-tune.':'Tap a quantity to adjust for this bake.'}</p>
            <TotalsBar rows={adjRows} bakeType={chosenRecipe.bake_type}/>
            <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 0.7fr",gap:4,marginBottom:6}}>
              {["ingredient","qty","unit"].map(h=><span key={h} style={{fontSize:9,letterSpacing:"0.06em",textTransform:"uppercase",color:C.muted,fontFamily:"Poppins,sans-serif"}}>{h}</span>)}
            </div>
            {ungrouped.map(renderAdjIng)}
            {groups.map(grp=>{
              const members=ings.filter(i=>!i._group&&i.groupId===grp.id);
              return(
                <div key={grp.id} style={{marginBottom:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6,marginTop:4}}>
                    <span style={{fontSize:10,letterSpacing:"0.06em",textTransform:"uppercase",color:C.muted,fontFamily:"Poppins,sans-serif"}}>{grp.name}</span>
                    <div style={{flex:1,height:0.5,background:C.border}}/>
                  </div>
                  <div style={{paddingLeft:12,borderLeft:`1.5px solid ${C.border}`}}>
                    {members.map(renderAdjIng)}
                  </div>
                </div>
              );
            })}
            {gridIsDrawable(chosenRecipe.method_grid,gridRows(ings))
              ?<div style={{marginTop:14,paddingTop:14,borderTop:`0.5px solid ${C.border}`}}>
                 <Label>method</Label>
                 <MethodGrid recipe={chosenRecipe} adj={form.adjusted_ingredients}/>
               </div>
              :chosenRecipe.method&&<div style={{marginTop:14,paddingTop:14,borderTop:`0.5px solid ${C.border}`}}><Label>method</Label><p style={{fontSize:12,color:C.ink,lineHeight:1.7,margin:0,whiteSpace:"pre-wrap"}}>{chosenRecipe.method}</p></div>}
          </div>
        );
      })()}
      {step===steps.length-1&&(
        <div>
          <div style={{marginBottom:20}}>
            <Label>bake notes</Label>
            <Tarea value={form.notes} onChange={v=>set("notes",v)} placeholder="What went well? What to improve?"/>
          </div>
          <div style={{borderTop:`0.5px solid ${C.border}`,paddingTop:20}}>
            <PhotoStrip
              photos={photos}
              onAdd={handleAddPhotos}
              onDelete={handleDeletePhoto}
              onCaption={handleCaptionSave}
              onReplace={handleReplacePhoto}
              uploading={photoUploading}
            />
          </div>
        </div>
      )}
      <div style={{display:"flex",gap:8,marginTop:20}}>
        <Btn onClick={onCancel}>cancel</Btn>
        {step>0&&<Btn onClick={()=>setStep(s=>s-1)}>back</Btn>}
        {step<steps.length-1
          ?<Btn primary onClick={()=>setStep(s=>s+1)} disabled={step===0&&!form.name.trim()}>next</Btn>
          :<Btn primary onClick={()=>{
              const pp=photos.filter(p=>p.pending);
              if(scaleOn&&effectiveRatio&&chosenRecipe&&!bake){setPendingSave(pp);setVariantPrompt(true);}
              else{onSave(form,pp);}
            }} disabled={!form.name.trim()}>save bake</Btn>}
      </div>

      {/* Variant-save prompt — appears after saving a scaled bake */}
      {variantPrompt&&(
        <div style={{position:"fixed",inset:0,background:"rgba(13,13,13,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:"0 20px",boxSizing:"border-box"}}>
          <div style={{background:C.parchment,borderRadius:16,padding:"24px 20px 28px",width:"100%",maxWidth:420,boxSizing:"border-box",fontFamily:"Poppins,sans-serif"}}>
            <p style={{fontSize:15,fontWeight:300,color:C.ink,margin:"0 0 8px"}}>Save as a variant recipe?</p>
            <p style={{fontSize:12,color:C.muted,margin:"0 0 20px",lineHeight:1.6}}>
              A new recipe will be created:{" "}
              <span style={{color:C.ink,fontStyle:"italic"}}>"{variantNameStr}"</span>
              <br/>You can rename it afterwards.
            </p>
            <div style={{display:"flex",gap:8}}>
              <Btn onClick={handleVariantNo}>no, discard</Btn>
              <Btn primary onClick={handleVariantYes}>yes, save</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ScorePanel({bake,onSave,onBack}){
  const [scores,setScores]=useState(()=>normalizeScores(bake.scores));
  const set=(k,v)=>setScores(s=>({...s,[k]:v}));
  return(
    <div style={{fontFamily:"Poppins,sans-serif"}}>
      <button onClick={onBack} style={{background:"none",border:"none",cursor:"pointer",color:C.muted,fontSize:11,padding:"0 0 20px",letterSpacing:"0.06em",textTransform:"uppercase"}}>← back</button>
      <p style={{fontSize:18,fontWeight:200,color:C.ink,margin:"0 0 4px"}}>{bake.name}</p>
      <p style={{fontSize:11,color:C.muted,margin:"0 0 24px"}}>{fmtDate(bake.date)}</p>
      <Label>assign scores · out of 5</Label>
      {SCORE_FIELDS.map(k=>(
        <div key={k} style={{marginBottom:16}}>
          <Label>{k}</Label>
          <ScoreBar label={k === 'taste' ? 'Taste' : 'Look'} value={scores[k]} onChange={v=>set(k,v)}/>
        </div>
      ))}
      <div style={{display:"flex",gap:8,marginTop:20}}>
        <Btn onClick={onBack}>cancel</Btn>
        <Btn primary onClick={()=>onSave({...bake,scores})}>save scores</Btn>
      </div>
    </div>
  );
}

// --- DetailView ---
function DetailView({bake,recipes,onBack,onEdit,onDelete,onAssignScore,uploadPhoto,deletePhoto,updatePhotoCaption}){
  const scores=normalizeScores(bake.scores);
  const sc=avg(bake.scores);
  const recipe=recipes.find(r=>r.id===bake.recipe_id);
  const ti=getBakeTypeInfo(bake.bake_type||"non-bread");
  const [photos,setPhotos]=useState([]);
  const [photoUploading,setPhotoUploading]=useState(false);
  const [exportOpen,setExportOpen]=useState(false);

  useEffect(()=>{
    loadPhotosForEntity('bake',bake.id).then(setPhotos);
  },[bake.id]);// eslint-disable-line

  async function handleAddPhotos(files){
    setPhotoUploading(true);
    for(const file of files){
      const photo=await uploadPhoto('bake',bake.id,file,'');
      if(photo) setPhotos(prev=>[...prev,photo]);
    }
    setPhotoUploading(false);
  }

  async function handleDeletePhoto(id){
    const photo=photos.find(p=>p.id===id);
    if(!photo) return;
    await deletePhoto(photo);
    setPhotos(prev=>prev.filter(p=>p.id!==id));
  }

  async function handleCaptionSave(id,caption){
    setPhotos(prev=>prev.map(p=>p.id===id?{...p,caption}:p));
    await updatePhotoCaption(id,caption);
  }

  async function handleReplacePhoto(id,newFile){
    await handleDeletePhoto(id);
    await handleAddPhotos([newFile]);
  }

  return(
    <div style={{fontFamily:"Poppins,sans-serif"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <button onClick={onBack} style={{background:"none",border:"none",cursor:"pointer",color:C.muted,fontSize:11,padding:0,letterSpacing:"0.06em",textTransform:"uppercase"}}>← back</button>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          {recipe&&<button onClick={()=>setExportOpen(true)} style={{background:"none",border:`0.5px solid ${C.border}`,borderRadius:7,cursor:"pointer",color:C.muted,fontSize:10,padding:"5px 10px",letterSpacing:"0.06em",textTransform:"uppercase",fontFamily:"Poppins,sans-serif"}}>export</button>}
          <button onClick={onEdit} style={{background:"none",border:`0.5px solid ${C.border}`,borderRadius:7,cursor:"pointer",color:C.muted,fontSize:10,padding:"5px 10px",letterSpacing:"0.06em",textTransform:"uppercase",fontFamily:"Poppins,sans-serif"}}>edit</button>
        </div>
      </div>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
        <span style={{fontSize:10,letterSpacing:"0.06em",textTransform:"uppercase",color:C.muted}}>{ti.label}</span>
      </div>
      <p style={{fontSize:22,fontWeight:200,color:C.ink,margin:"0 0 4px"}}>{bake.name}</p>
      <p style={{fontSize:10,color:C.muted,letterSpacing:"0.06em",textTransform:"uppercase",margin:"0 0 2px"}}>{bake.category}</p>
      <p style={{fontSize:11,color:C.muted,margin:"0 0 20px"}}>{fmtDate(bake.date)}</p>
      {recipe&&(
        <div style={{marginBottom:20}}>
          <Label>recipe used</Label>
          <p style={{fontSize:13,color:C.ink,margin:"0 0 10px"}}>{recipe.name}{recipe.variation_name?` — ${recipe.variation_name}`:""}</p>
          <IngView recipe={recipe} adj={bake.adjusted_ingredients||{}}/>
        </div>
      )}
      <div style={{background:C.sandLight,borderRadius:10,padding:16,marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          <Label>scores</Label>
          <button onClick={onAssignScore} style={{background:C.wheat,border:"none",borderRadius:6,cursor:"pointer",color:"#fff",fontSize:10,padding:"5px 10px",fontFamily:"Poppins,sans-serif",letterSpacing:"0.04em",textTransform:"uppercase"}}>assign score</button>
        </div>
        {SCORE_FIELDS.map(k=>(
          <div key={k} style={{display:"flex",alignItems:"center",marginBottom:8}}>
            <span style={{fontSize:12,color:C.ink,textTransform:"capitalize",width:90,fontFamily:"Poppins,sans-serif"}}>{k}</span>
            <div style={{flex:1,height:3,background:C.border,borderRadius:2,marginRight:12,position:"relative"}}>
              <div style={{position:"absolute",left:0,top:0,height:"100%",width:`${(scores[k]-1)/4*100}%`,background:C.wheat,borderRadius:2}}/>
            </div>
            <span style={{fontSize:12,color:C.ink,minWidth:36,textAlign:"right",fontFamily:"Poppins,sans-serif"}}>{scores[k]} / 5</span>
          </div>
        ))}
        <div style={{borderTop:`0.5px solid ${C.border}`,marginTop:10,paddingTop:10,display:"flex",justifyContent:"space-between"}}>
          <span style={{fontSize:10,color:C.muted,textTransform:"uppercase",letterSpacing:"0.06em",fontFamily:"Poppins,sans-serif"}}>average</span>
          <span style={{fontSize:15,fontWeight:300,color:C.wheat,fontFamily:"Poppins,sans-serif"}}>{sc.toFixed(1)} / 5</span>
        </div>
      </div>
      {bake.notes&&<div style={{marginBottom:20}}><Label>notes</Label><p style={{fontSize:13,color:C.ink,lineHeight:1.7,whiteSpace:"pre-wrap",margin:0,fontFamily:"Poppins,sans-serif"}}>{bake.notes}</p></div>}
      <div style={{borderTop:`0.5px solid ${C.border}`,paddingTop:20,marginBottom:20}}>
        <PhotoStrip
          photos={photos}
          onAdd={handleAddPhotos}
          onDelete={handleDeletePhoto}
          onCaption={handleCaptionSave}
          onReplace={handleReplacePhoto}
          uploading={photoUploading}
        />
      </div>
      <button onClick={()=>onDelete(bake.id)} style={{background:"none",border:"none",cursor:"pointer",color:"#c0392b",fontSize:10,padding:0,letterSpacing:"0.06em",textTransform:"uppercase",fontFamily:"Poppins,sans-serif"}}>delete bake</button>
      {exportOpen&&recipe&&<ExportModal recipe={recipe} adj={bake.adjusted_ingredients||{}} filename={(bake.name||'bake').replace(/[^a-z0-9]/gi,'_').toLowerCase()} onClose={()=>setExportOpen(false)}/>}
    </div>
  );
}

function CalendarTab({bakes,onSelectBake}){
  const now=new Date();
  const [year,setYear]=useState(now.getFullYear());
  const [month,setMonth]=useState(now.getMonth());
  const [hovered,setHovered]=useState(null);
  const [popup,setPopup]=useState(null);
  const dim=new Date(year,month+1,0).getDate();
  const firstDay=(new Date(year,month,1).getDay()+6)%7;
  const mName=new Date(year,month,1).toLocaleDateString("en-GB",{month:"long",year:"numeric"});
  const todayKey=today();
  const byDate={};
  bakes.forEach(b=>{if(!byDate[b.date]) byDate[b.date]=[];byDate[b.date].push(b);});
  const dk=d=>`${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
  return(
    <div style={{fontFamily:"Poppins,sans-serif",position:"relative"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <button onClick={()=>{if(month===0){setMonth(11);setYear(y=>y-1);}else setMonth(m=>m-1);}} style={{background:"none",border:"none",cursor:"pointer",color:C.muted,fontSize:18,padding:"0 6px"}}>‹</button>
        <span style={{fontSize:13,fontWeight:300,color:C.ink}}>{mName}</span>
        <button onClick={()=>{if(month===11){setMonth(0);setYear(y=>y+1);}else setMonth(m=>m+1);}} style={{background:"none",border:"none",cursor:"pointer",color:C.muted,fontSize:18,padding:"0 6px"}}>›</button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3,marginBottom:6}}>
        {["M","T","W","T","F","S","S"].map((d,i)=><div key={i} style={{textAlign:"center",fontSize:9,letterSpacing:"0.06em",color:C.muted}}>{d}</div>)}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3}}>
        {Array(firstDay).fill(null).map((_,i)=><div key={`e${i}`}/>)}
        {Array(dim).fill(null).map((_,i)=>{
          const d=i+1,k=dk(d),bs=byDate[k]||[];
          const isToday=k===todayKey,hasBake=bs.length>0,isHov=hovered===k;
          return(
            <div key={d} style={{position:"relative"}}
              onMouseEnter={()=>hasBake&&setHovered(k)}
              onMouseLeave={()=>setHovered(null)}
              onClick={()=>hasBake&&setPopup({k,bs})}>
              <div style={{aspectRatio:"1",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",borderRadius:8,cursor:hasBake?"pointer":"default",background:isToday?C.sandLight:isHov&&hasBake?"#F0EBE0":"transparent",border:isToday?`0.5px solid ${C.sand}`:"none"}}>
                <span style={{fontSize:11,color:hasBake?C.ink:C.muted,fontWeight:hasBake?400:300}}>{d}</span>
                {hasBake&&<div style={{width:5,height:5,borderRadius:"50%",background:C.wheat,margin:"2px auto 0"}}/>}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{marginTop:16,display:"flex",gap:12,flexWrap:"wrap"}}>
        {BAKE_TYPES.map(t=>(
          <div key={t.id} style={{display:"flex",alignItems:"center",gap:5}}>
            <div style={{width:7,height:7,borderRadius:"50%",background:C.wheat}}/>
            <span style={{fontSize:10,color:C.muted,fontFamily:"Poppins,sans-serif"}}>{t.label}</span>
          </div>
        ))}
      </div>
      {popup&&(
        <div style={{position:"fixed",inset:0,background:"rgba(13,13,13,0.4)",display:"flex",alignItems:"flex-end",justifyContent:"center",zIndex:100}} onClick={()=>setPopup(null)}>
          <div onClick={e=>e.stopPropagation()} style={{background:C.parchment,borderRadius:"16px 16px 0 0",padding:"20px 20px 32px",width:"100%",maxWidth:420,boxSizing:"border-box"}}>
            <p style={{fontSize:11,letterSpacing:"0.06em",textTransform:"uppercase",color:C.muted,margin:"0 0 14px"}}>{fmtDate(popup.k)}</p>
            {popup.bs.map(b=>(
              <div key={b.id} onClick={()=>{setPopup(null);onSelectBake(b);}}
                style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 0",borderBottom:`0.5px solid ${C.border}`,cursor:"pointer"}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div>
                    <p style={{margin:0,fontSize:14,fontWeight:300,color:C.ink,fontFamily:"Poppins,sans-serif"}}>{b.name}</p>
                    <p style={{margin:"2px 0 0",fontSize:10,color:C.muted,fontFamily:"Poppins,sans-serif",letterSpacing:"0.04em",textTransform:"uppercase"}}>{b.category}</p>
                  </div>
                </div>
                <span style={{fontSize:16,fontWeight:200,color:C.wheat,fontFamily:"Poppins,sans-serif"}}>{avg(b.scores).toFixed(1)} / 5</span>
              </div>
            ))}
            <button onClick={()=>setPopup(null)} style={{marginTop:16,width:"100%",padding:"10px 0",background:"none",border:`0.5px solid ${C.border}`,borderRadius:8,fontSize:11,color:C.muted,cursor:"pointer",fontFamily:"Poppins,sans-serif",letterSpacing:"0.06em",textTransform:"uppercase"}}>close</button>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Main Journal ---
export default function Journal({session}){
  const [recipes,setRecipes]=useState([]);
  const [bakes,setBakes]=useState([]);
  const [categories,setCategories]=useState([]);
  const [tab,setTab]=useState("recipe");
  const [sourceTab,setSourceTab]=useState("recipe");
  const [creating,setCreating]=useState(false);
  const [catOpen,setCatOpen]=useState(false);
  const [browseMode,setBrowseMode]=useState("date");
  const [filterCat,setFilterCat]=useState("all");
  // Recipe filters
  const [recipeSearch,setRecipeSearch]=useState("");
  const [recipeCatFilter,setRecipeCatFilter]=useState("all");
  const [recipeSort,setRecipeSort]=useState("az");
  // Journal filters
  const [journalSearch,setJournalSearch]=useState("");
  const [journalCatFilter,setJournalCatFilter]=useState("all");
  const [journalMonthFilter,setJournalMonthFilter]=useState("all");
  const [journalSort,setJournalSort]=useState("date-desc");
  const [selected,setSelected]=useState(null);
  const [logging,setLogging]=useState(false);
  const [editingBake,setEditingBake]=useState(null);
  const [scoringBake,setScoringBake]=useState(null);

  const load=useCallback(async()=>{
    const uid=session.user.id;
    const [rRes,bRes,cRes]=await Promise.all([
      supabase.from("recipes").select("*").eq("user_id",uid).order("created_at",{ascending:false}),
      supabase.from("bakes").select("*").eq("user_id",uid).order("date",{ascending:false}),
      supabase.from("categories").select("*").eq("user_id",uid).order("name"),
    ]);
    if(rRes.data) setRecipes(rRes.data);
    if(bRes.data) setBakes(bRes.data);
    if(cRes.data) setCategories(cRes.data.map(c=>c.name));
  },[session]);

  useEffect(()=>{load();},[load]);

  // Photo helpers
  async function uploadPhoto(entityType,entityId,file,caption){
    const uid=session.user.id;
    const ext=(file.name.split('.').pop()||'jpg').toLowerCase();
    const path=`${uid}/${entityType}/${entityId}/${Date.now()}.${ext}`;
    const {error:storageErr}=await supabase.storage.from('photos').upload(path,file,{contentType:file.type});
    if(storageErr){console.error('Photo upload failed:',storageErr);return null;}
    const {data:{publicUrl}}=supabase.storage.from('photos').getPublicUrl(path);
    const col=entityType==='recipe'?'recipe_id':'bake_id';
    const {data}=await supabase.from('photos')
      .insert({user_id:uid,[col]:entityId,storage_path:path,caption:caption||''})
      .select().single();
    if(!data) return null;
    return {id:data.id,url:publicUrl,caption:data.caption||'',storage_path:path,pending:false};
  }

  async function deletePhoto(photo){
    await supabase.storage.from('photos').remove([photo.storage_path]);
    await supabase.from('photos').delete().eq('id',photo.id);
  }

  async function updatePhotoCaption(photoId,caption){
    await supabase.from('photos').update({caption}).eq('id',photoId);
  }

  const photoHelpers={uploadPhoto,deletePhoto,updatePhotoCaption};

  async function handleSaveRecipe(r,pendingPhotos=[]){
    const uid=session.user.id;
    const payload={...r,user_id:uid,ingredients:r.ingredients||[]};
    delete payload.id;
    let savedId=r.id;
    let error;
    if(r.id){
      ({error}=await supabase.from("recipes").update(payload).eq("id",r.id));
    } else {
      const {data,error:e}=await supabase.from("recipes").insert(payload).select("id").single();
      error=e;
      if(data) savedId=data.id;
    }
    if(error){console.error("Recipe save failed:",error);return false;}
    for(const pf of pendingPhotos){
      await uploadPhoto('recipe',savedId,pf.file,pf.caption);
    }
    await load();
    return true;
  }

  async function handleDeleteRecipe(id){
    await supabase.from("recipes").delete().eq("id",id);
    load();
  }

  async function handleAddCategory(name){
    await supabase.from("categories").insert({user_id:session.user.id,name});
    load();
  }

  async function handleDeleteCategory(name){
    await supabase.from("categories").delete().eq("user_id",session.user.id).eq("name",name);
    load();
  }

  async function handleSaveBake(form,pendingPhotos=[]){
    const uid=session.user.id;
    const bt=form.recipe_id?(recipes.find(r=>r.id===form.recipe_id)?.bake_type||"non-bread"):"non-bread";
    let notesSummary=form.notes_summary||"";
    if(form.notes&&form.notes.trim()){
      try{
        const res=await fetch("/api/import-recipe",{
          method:"POST",headers:{"Content-Type":"application/json"},
          body:JSON.stringify({model:"claude-haiku-4-5-20251001",max_tokens:60,
            messages:[{role:"user",content:`Summarise this baker's note in 12 words or fewer, plain text only, no quotes:\n\n${form.notes}`}]})
        });
        const d=await res.json();
        notesSummary=d.content?.[0]?.text?.trim()||"";
      }catch{}
    }
    const payload={
      user_id:uid,name:form.name,category:form.category,bake_type:bt,
      recipe_id:form.recipe_id||null,date:form.date,notes:form.notes,
      notes_summary:notesSummary,adjusted_ingredients:form.adjusted_ingredients||{},
      scores:form.scores,
    };
    let savedBakeId=form.id;
    if(form.id){
      await supabase.from("bakes").update(payload).eq("id",form.id);
    } else {
      const {data}=await supabase.from("bakes").insert(payload).select("id").single();
      if(data) savedBakeId=data.id;
    }
    if(savedBakeId){
      for(const pf of pendingPhotos){
        await uploadPhoto('bake',savedBakeId,pf.file,pf.caption);
      }
    }
    setLogging(false);setEditingBake(null);setSelected(null);
    load();
  }

  async function handleSaveScores(bake){
    await supabase.from("bakes").update({scores:bake.scores}).eq("id",bake.id);
    setScoringBake(null);setSelected(bake);
    load();
  }

  async function handleDeleteBake(id){
    await supabase.from("bakes").delete().eq("id",id);
    setSelected(null);
    load();
  }

  async function handleSignOut(){
    await supabase.auth.signOut();
  }

  // Journal derived data
  const journalMonths=Array.from(new Set(bakes.map(b=>b.date?.slice(0,7)).filter(Boolean))).sort((a,b)=>b.localeCompare(a));
  function fmtMonth(ym){const[y,m]=ym.split("-");const mo=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][parseInt(m,10)-1];return `${mo} ${y.slice(2)}`;}
  const journalFiltered=bakes.filter(b=>{
    if(journalCatFilter!=="all"&&b.category!==journalCatFilter) return false;
    if(journalMonthFilter!=="all"&&!(b.date||"").startsWith(journalMonthFilter)) return false;
    if(journalSearch.trim()&&!b.name.toLowerCase().includes(journalSearch.trim().toLowerCase())) return false;
    return true;
  });
  const journalDisplayed=[...journalFiltered].sort((a,b)=>{
    if(journalSort==="az") return a.name.localeCompare(b.name);
    if(journalSort==="za") return b.name.localeCompare(a.name);
    if(journalSort==="date-asc") return new Date(a.date)-new Date(b.date);
    return new Date(b.date)-new Date(a.date);
  });
  const TABS=[{id:"recipe",label:"recipe"},{id:"journal",label:"journal"}];
  const showMain=!logging&&!editingBake&&!selected&&!scoringBake&&!creating;
  const data={recipes,categories};

  async function handleCreateRecipe(r,pendingPhotos=[]){
    const ok=await handleSaveRecipe(r,pendingPhotos);
    if(ok!==false) setCreating(false);
  }

  return(
    <div style={{background:C.parchment,minHeight:"100vh",maxWidth:420,margin:"0 auto",padding:"28px 20px 80px",fontFamily:"Poppins,sans-serif"}}>
      {showMain&&(
        <div style={{marginBottom:24,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <p style={{fontSize:22,fontWeight:200,color:C.ink,margin:0,letterSpacing:"-0.02em"}}>homer</p>
          <div style={{display:"flex",alignItems:"center",gap:0}}>
            {tab==="recipe"&&(
              <>
                <button onClick={()=>setCreating(true)} style={{background:"none",border:`0.5px solid ${C.border}`,borderRadius:7,cursor:"pointer",color:C.muted,fontSize:10,padding:"5px 10px",fontFamily:"Poppins,sans-serif",letterSpacing:"0.04em",marginRight:6}}>+ recipe</button>
                <button onClick={()=>setCatOpen(o=>!o)} style={{background:"none",border:`0.5px solid ${C.border}`,borderRadius:7,cursor:"pointer",color:catOpen?C.wheat:C.muted,fontSize:10,padding:"5px 10px",fontFamily:"Poppins,sans-serif",letterSpacing:"0.04em",marginRight:6}}>category</button>
              </>
            )}
            {tab==="journal"&&(
              <button onClick={()=>setLogging(true)} style={{background:"none",border:`0.5px solid ${C.border}`,borderRadius:7,cursor:"pointer",color:C.muted,fontSize:10,padding:"5px 10px",fontFamily:"Poppins,sans-serif",letterSpacing:"0.04em",marginRight:6}}>+ log</button>
            )}
            <button onClick={handleSignOut} style={{background:"none",border:`0.5px solid ${C.border}`,borderRadius:7,cursor:"pointer",color:C.muted,fontSize:10,padding:"5px 10px",fontFamily:"Poppins,sans-serif",letterSpacing:"0.04em"}}>sign out</button>
          </div>
        </div>
      )}
      {creating&&<RecipeEditor categories={categories} onSave={handleCreateRecipe} onCancel={()=>setCreating(false)} {...photoHelpers}/>}
      {scoringBake&&<ScorePanel bake={scoringBake} onSave={handleSaveScores} onBack={()=>{setScoringBake(null);setSelected(scoringBake);}}/>}
      {selected&&!logging&&!editingBake&&!scoringBake&&(
        <DetailView bake={selected} recipes={recipes}
          onBack={()=>{setSelected(null);setTab(sourceTab);}}
          onEdit={()=>{setEditingBake(selected);setSelected(null);}}
          onDelete={handleDeleteBake}
          onAssignScore={()=>{setScoringBake(selected);setSelected(null);}}
          {...photoHelpers}/>
      )}
      {(logging||editingBake)&&!scoringBake&&(
        <LogForm data={data} bake={editingBake} onSave={handleSaveBake} onCancel={()=>{setLogging(false);setEditingBake(null);}} onSaveVariant={handleSaveRecipe} {...photoHelpers}/>
      )}
      {showMain&&(
        <>
          <div style={{display:"flex",gap:0,marginBottom:24,borderBottom:`0.5px solid ${C.border}`}}>
            {TABS.map(t=>(
              <button key={t.id} onClick={()=>{setTab(t.id);setCatOpen(false);}} style={{flex:1,padding:"10px 0",background:"none",border:"none",borderBottom:tab===t.id?`1.5px solid ${C.wheat}`:"1.5px solid transparent",cursor:"pointer",fontSize:10,letterSpacing:"0.06em",textTransform:"uppercase",color:tab===t.id?C.wheat:C.muted,fontFamily:"Poppins,sans-serif",marginBottom:-1}}>{t.label}</button>
            ))}
          </div>
          {tab==="recipe"&&<CatManager categories={categories} onAdd={handleAddCategory} onDelete={handleDeleteCategory} open={catOpen} onClose={()=>setCatOpen(false)}/>}
          {tab==="recipe"&&<RecipeTab recipes={recipes} categories={categories} onSave={handleSaveRecipe} onDelete={handleDeleteRecipe} search={recipeSearch} onSearchChange={setRecipeSearch} catFilter={recipeCatFilter} onCatFilterChange={setRecipeCatFilter} sort={recipeSort} onSortChange={setRecipeSort} {...photoHelpers}/>}
          {tab==="journal"&&(
            <>
              <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
                <input value={journalSearch} onChange={e=>setJournalSearch(e.target.value)} placeholder="search by name"
                  style={{width:"100%",boxSizing:"border-box",padding:"8px 12px",border:`0.5px solid ${C.border}`,borderRadius:8,fontFamily:"Poppins,sans-serif",fontSize:12,color:C.ink,background:C.white,outline:"none"}}/>
                <div style={{display:"flex",gap:8}}>
                  <select value={journalCatFilter} onChange={e=>setJournalCatFilter(e.target.value)}
                    style={{flex:1,padding:"7px 8px",border:`0.5px solid ${C.border}`,borderRadius:8,fontFamily:"Poppins,sans-serif",fontSize:11,color:C.ink,background:C.white,outline:"none"}}>
                    <option value="all">all categories</option>
                    {categories.map(c=><option key={c} value={c}>{c}</option>)}
                  </select>
                  <select value={journalMonthFilter} onChange={e=>setJournalMonthFilter(e.target.value)}
                    style={{flex:1,padding:"7px 8px",border:`0.5px solid ${C.border}`,borderRadius:8,fontFamily:"Poppins,sans-serif",fontSize:11,color:C.ink,background:C.white,outline:"none"}}>
                    <option value="all">all months</option>
                    {journalMonths.map(ym=><option key={ym} value={ym}>{fmtMonth(ym)}</option>)}
                  </select>
                  <select value={journalSort} onChange={e=>setJournalSort(e.target.value)}
                    style={{flex:1,padding:"7px 8px",border:`0.5px solid ${C.border}`,borderRadius:8,fontFamily:"Poppins,sans-serif",fontSize:11,color:C.ink,background:C.white,outline:"none"}}>
                    <option value="date-desc">newest first</option>
                    <option value="date-asc">oldest first</option>
                    <option value="az">name A–Z</option>
                    <option value="za">name Z–A</option>
                  </select>
                </div>
              </div>
              {journalDisplayed.length===0
                ?<p style={{fontSize:13,color:C.muted,textAlign:"center",marginTop:40}}>No bakes found.</p>
                :journalDisplayed.map(b=>(
                  <div key={b.id} onClick={()=>{setSourceTab("journal");setSelected(b);}}
                    style={{background:C.white,border:`0.5px solid ${C.border}`,borderRadius:12,padding:"14px 16px",cursor:"pointer",marginBottom:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:3}}>
                          <p style={{margin:0,fontSize:15,fontWeight:300,color:C.ink,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{b.name}</p>
                        </div>
                        <p style={{margin:0,fontSize:10,color:C.muted,letterSpacing:"0.06em",textTransform:"uppercase"}}>{b.category}</p>
                        <p style={{margin:"4px 0 0",fontSize:11,color:C.muted}}>{fmtDate(b.date)}</p>
                        {(b.notes_summary||b.notes)&&(
                          <p style={{margin:"6px 0 0",fontSize:11,color:C.muted,fontStyle:"italic",lineHeight:1.5}}>
                            {b.notes_summary||b.notes.slice(0,80)+(b.notes.length>80?"…":"")}
                          </p>
                        )}
                      </div>
                      <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:6,flexShrink:0,marginLeft:10}}>
                        <div style={{textAlign:"right"}}>
                          <p style={{margin:0,fontSize:18,fontWeight:200,color:C.wheat}}>{avg(b.scores).toFixed(1)}</p>
                          <p style={{margin:0,fontSize:10,color:C.muted}}>avg / 5</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              }
            </>
          )}
          {tab==="calendar"&&<CalendarTab bakes={bakes} onSelectBake={b=>{setSourceTab("calendar");setSelected(b);}}/>}
        </>
      )}
    </div>
  );
}


